<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { socketConnected } from '@/composables/useAdminSocket';
import AnalyticsKpiCard from '@/components/analytics/AnalyticsKpiCard.vue';
import AnalyticsBarChart from '@/components/analytics/AnalyticsBarChart.vue';
import AnalyticsDonutChart from '@/components/analytics/AnalyticsDonutChart.vue';
import AnalyticsLineChart from '@/components/analytics/AnalyticsLineChart.vue';
import {
  useAnalyticsData,
  fmtDec,
  type Period,
} from '@/composables/useAnalyticsData';
import { useOrdersStats } from '@/composables/useOrdersStats';
import { usePaymentsStats } from '@/composables/usePaymentsStats';
import { rolesArray } from '@/refs/common.refs';

const { t } = useI18n({ useScope: 'global' });

// analytics_viewer reçoit un 403 du serveur sur les données de commandes :
// l'onglet est masqué plutôt que de laisser l'erreur s'afficher.
const isAnalyticsViewerOnly = computed(() => {
  const roles = (rolesArray.value ?? []) as string[];
  return roles.length === 1 && roles[0] === 'analytics_viewer';
});

// ── Période ───────────────────────────────────────────────────────────────────
const period = ref<Period>('7d');
const PERIODS = computed<{ key: Period; label: string }[]>(() => [
  { key: '1d', label: t('analytics.periods.1d') },
  { key: '7d', label: t('analytics.periods.7d') },
  { key: '3m', label: t('analytics.periods.3m') },
  { key: '6m', label: t('analytics.periods.6m') },
  { key: '1y', label: t('analytics.periods.1y') },
]);

// ── Custom date range ─────────────────────────────────────────────────────────
const isCustom = ref(false);
const customPickerOpen = ref(false);

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}
function sevenDaysAgoStr() {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return d.toISOString().slice(0, 10);
}
const customFrom = ref(sevenDaysAgoStr());
const customTo = ref(todayStr());
const customFromTemp = ref(customFrom.value);
const customToTemp = ref(customTo.value);

function openCustomPicker() {
  customFromTemp.value = customFrom.value;
  customToTemp.value = customTo.value;
  customPickerOpen.value = true;
}
function applyCustomRange() {
  if (!customFromTemp.value || !customToTemp.value) return;
  customFrom.value = customFromTemp.value;
  customTo.value = customToTemp.value;
  isCustom.value = true;
  customPickerOpen.value = false;
}
function cancelCustomPicker() {
  customPickerOpen.value = false;
}
function selectPreset(key: Period) {
  period.value = key;
  isCustom.value = false;
  customPickerOpen.value = false;
}

const customLabel = computed(() => {
  if (!isCustom.value) return t('analytics.datePicker.custom');
  const fmt = (s: string) => s.slice(5).replace('-', '/');
  return `${fmt(customFrom.value)} → ${fmt(customTo.value)}`;
});

function startOf(p: Period): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (p === '1d') return d;
  if (p === '7d') {
    d.setDate(d.getDate() - 6);
    return d;
  }
  if (p === '3m') {
    d.setMonth(d.getMonth() - 3);
    return d;
  }
  if (p === '6m') {
    d.setMonth(d.getMonth() - 6);
    return d;
  }
  d.setFullYear(d.getFullYear() - 1);
  return d;
}
const fromISO = computed(() =>
  isCustom.value
    ? new Date(customFrom.value).toISOString()
    : startOf(period.value).toISOString(),
);
const toISO = computed(() =>
  isCustom.value
    ? new Date(customTo.value + 'T23:59:59').toISOString()
    : new Date().toISOString(),
);

// ── Onglets ───────────────────────────────────────────────────────────────────
type Tab = 'orgs' | 'stores' | 'orders' | 'payments';
const activeTab = ref<Tab>('orgs');
const TABS = computed<{ key: Tab; label: string; icon: string }[]>(() => {
  const all = [
    {
      key: 'orgs' as const,
      label: t('analytics.tabs.orgs'),
      icon: 'pi-building',
    },
    {
      key: 'stores' as const,
      label: t('analytics.tabs.stores'),
      icon: 'pi-shop',
    },
    {
      key: 'orders' as const,
      label: t('analytics.tabs.orders'),
      icon: 'pi-shopping-cart',
    },
    {
      key: 'payments' as const,
      label: t('analytics.tabs.payments'),
      icon: 'pi-wallet',
    },
  ];
  return isAnalyticsViewerOnly.value
    ? all.filter((tab) => tab.key !== 'orders')
    : all;
});

watch(
  TABS,
  (tabs) => {
    if (!tabs.some((tab) => tab.key === activeTab.value)) {
      activeTab.value = tabs[0]?.key ?? 'orgs';
    }
  },
  { immediate: true },
);

// ── Data ──────────────────────────────────────────────────────────────────────
const orgFilter = ref<string | null>(null);
const storeFilter = ref<string | null>(null);
const nullRef = ref<string | null>(null); // filtre vide permanent pour charger tous les stores

const activeCustomFrom = computed(() =>
  isCustom.value ? customFrom.value : undefined,
);
const activeCustomTo = computed(() =>
  isCustom.value ? customTo.value : undefined,
);

const {
  allOrgs,
  orgStores,
  organizations,
  stores: orgFilteredStores,
  isPending,
  isError,
  storesPending: orgStoresPending,
} = useAnalyticsData(
  period,
  orgFilter,
  storeFilter,
  activeCustomFrom as any,
  activeCustomTo as any,
);

// Tous les commerces sans filtre org (pour l'onglet Commerces et Commandes)
const { stores: allStoresUnfiltered, isPending: allStoresPending } =
  useAnalyticsData(
    period,
    nullRef,
    nullRef,
    activeCustomFrom as any,
    activeCustomTo as any,
  );

// Mode ranking : 'ranking' (liste) ou 'evolution' (graphique — défaut)
type RankMode = 'ranking' | 'evolution';
const rankMode = ref<RankMode>('evolution');

// Animation de scroll maison : un rAF loop qu'on peut annuler proprement par
// conteneur. Nécessaire car deux appels natifs container.scrollTo({behavior:
// 'smooth'}) qui se chevauchent (clics rapprochés) se marchent dessus et
// produisent un petit à-coup au lieu de rediriger fluidement vers la
// nouvelle cible — ici un nouvel appel annule proprement l'animation en
// cours avant d'en lancer une autre.
const scrollAnimations = new WeakMap<HTMLElement, number>();
function animateScrollTo(container: HTMLElement, target: number) {
  const running = scrollAnimations.get(container);
  if (running) cancelAnimationFrame(running);
  const start = container.scrollTop;
  const distance = target - start;
  if (Math.abs(distance) < 1) return;
  const duration = 320;
  const startTime = performance.now();
  const ease = (t: number) => 1 - (1 - t) * (1 - t); // ease-out quad
  const step = (now: number) => {
    const elapsed = now - startTime;
    const t = Math.min(1, elapsed / duration);
    container.scrollTop = start + distance * ease(t);
    if (t < 1) {
      scrollAnimations.set(container, requestAnimationFrame(step));
    } else {
      scrollAnimations.delete(container);
    }
  };
  scrollAnimations.set(container, requestAnimationFrame(step));
}

// Fait défiler en douceur l'élément cliqué (ligne de liste) dans la zone
// visible de son conteneur .ap__list — appelé sur les sélections
// orgs/commerces/commandes pour que l'utilisateur voie clairement quelle
// ligne vient d'être sélectionnée. On scrolle uniquement ce conteneur
// (jamais la fenêtre) : scrollIntoView peut sinon remonter des ancêtres
// scrollables imprévus et faire "sauter" toute la page.
// Verrouille le scroll de la fenêtre à sa position actuelle pendant
// `durationMs` : sur l'onglet Organisation, sélectionner une org déclenche
// un refetch (analytics-store-stats) dont la réponse change la hauteur de
// la colonne de droite un instant après le clic (apparition/disparition de
// la carte "Stores de cette org" pendant que le bouton cliqué garde le
// focus). Le navigateur compense alors ce changement de layout en
// re-scrollant la fenêtre pour garder l'élément focus visible — d'où le
// "saut" de toute la page. Commerces/Commandes n'ont pas ce refetch
// différé, donc pas ce souci. On rétablit de force window.scrollY à sa
// valeur au clic à chaque event scroll pendant cette fenêtre de temps.
let windowScrollLockTimer: ReturnType<typeof setTimeout> | null = null;
function lockWindowScroll(durationMs: number) {
  const lockedY = window.scrollY;
  const onScroll = () => {
    if (window.scrollY !== lockedY) window.scrollTo(0, lockedY);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  if (windowScrollLockTimer) clearTimeout(windowScrollLockTimer);
  windowScrollLockTimer = setTimeout(() => {
    window.removeEventListener('scroll', onScroll);
    windowScrollLockTimer = null;
  }, durationMs);
}

// Verrouille la hauteur de .ap__main (colonne de droite, onglet Organisation)
// à sa valeur actuelle pendant tout le temps du refetch déclenché par la
// sélection d'une nouvelle org : la carte "Stores de cette org" apparaît/
// disparaît selon orgStoresPending, ce qui change la hauteur de la page et
// perturbe le scroll (de la fenêtre ou du navigateur suivant le focus),
// quel que soit le mécanisme exact en cause. Fixer une hauteur minimale
// stable pendant le chargement élimine le saut à la source plutôt que
// d'essayer de compenser après coup.
const orgMainRef = ref<HTMLElement | null>(null);
const orgMainMinHeight = ref<number | null>(null);
watch(orgStoresPending, (pending) => {
  if (!pending) {
    orgMainMinHeight.value = null;
  }
});
function lockOrgMainHeight() {
  if (orgMainRef.value) {
    orgMainMinHeight.value = orgMainRef.value.offsetHeight;
  }
}

// L'org sélectionnée est réordonnée en tête de filteredOrgs (voir plus bas) :
// elle est donc toujours en position 0 de la page 1, mais le scroll visuel
// de .ap__list ne suit pas cette réorganisation tout seul si on avait
// scrollé plus bas dans la liste avant de cliquer — on le ramène en haut
// explicitement pour que l'élément fraîchement déplacé reste visible.
function scrollOrgListToTop(e: Event) {
  const el = e.currentTarget as HTMLElement | null;
  const container = el?.closest('.ap__list') as HTMLElement | null;
  if (container) animateScrollTo(container, 0);
}

function scrollSelectedIntoView(e: Event) {
  const el = e.currentTarget as HTMLElement | null;
  const container = el?.closest('.ap__list') as HTMLElement | null;
  if (!el || !container) return;
  el.blur();
  lockWindowScroll(600);
  // nextTick : laisse Vue terminer le re-render (classe --active, largeur
  // potentiellement changée) avant de mesurer, sinon les positions lues
  // peuvent être celles d'avant le clic.
  nextTick(() => {
    // getBoundingClientRect plutôt que offsetTop : offsetTop est relatif au
    // premier ancêtre positionné (offsetParent), qui n'est pas forcément
    // .ap__list — un décalage silencieux qui ne se remarque qu'au milieu de
    // la liste (en haut/bas l'erreur reste dans la marge ou saute de toute
    // façon assez pour "avoir l'air" de fonctionner).
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const elTop = elRect.top - containerRect.top + container.scrollTop;
    const elBottom = elTop + el.offsetHeight;
    const viewTop = container.scrollTop;
    const viewBottom = viewTop + container.clientHeight;
    if (elTop < viewTop) {
      animateScrollTo(container, elTop);
    } else if (elBottom > viewBottom) {
      animateScrollTo(container, elBottom - container.clientHeight);
    }
  });
}

// ── Onglet Orgs : sélection + recherche ──────────────────────────────────────
const orgSearch = ref('');
const selectedOrgId = ref<string | null>(null);
const selectedOrg = computed(
  () =>
    organizations.value.find((o) => o.organizationId === selectedOrgId.value) ??
    null,
);

const filteredOrgs = computed(() => {
  const q = orgSearch.value.trim().toLowerCase();
  const list = !q
    ? organizations.value
    : organizations.value.filter((o) => o.name.toLowerCase().includes(q));
  // L'org sélectionnée reste toujours en tête : évite d'avoir à scroller
  // dans la liste pour la retrouver, et rend inutile tout calcul de scroll
  // fragile vers un élément qui pourrait être n'importe où au milieu.
  if (!selectedOrgId.value) return list;
  const idx = list.findIndex((o) => o.organizationId === selectedOrgId.value);
  if (idx <= 0) return list;
  const copy = [...list];
  const [selected] = copy.splice(idx, 1);
  copy.unshift(selected);
  return copy;
});

// Stores de l'org sélectionnée (depuis storeData via orgFilter)
watch(
  selectedOrgId,
  (id) => {
    orgFilter.value = id;
    rankMode.value = 'evolution';
  },
  { immediate: true },
);

watch(
  organizations,
  (list) => {
    if (list.length && !selectedOrgId.value)
      selectedOrgId.value = list[0].organizationId;
  },
  { immediate: true },
);

// KPIs org sélectionnée — 3 colonnes × 2 lignes = 6 cards
const orgKpis = computed(() => {
  const o = selectedOrg.value;
  if (!o) return [];
  const w = o.windowed;
  const s = o.snapshot;
  const total = w.orderCount;
  const terminated = w.ordersByStatus.terminated ?? 0;
  const pending = w.ordersByStatus.pending ?? 0;
  const inProgress = w.ordersByStatus.in_progress ?? 0;
  const ready = w.ordersByStatus.ready ?? 0;
  const rejected = w.ordersByStatus.rejected ?? 0;
  const compRate = total ? Math.round((terminated / total) * 100) : 0;
  const paidPct = total ? Math.round((w.paidOrderCount / total) * 100) : 0;
  const pmtConf = s.paymentConfiguredStoreCount ?? 0;
  return [
    {
      icon: 'pi-euro',
      label: t('analytics.kpis.revenue'),
      value: fmtDec(w.revenue),
      desc: `${t('analytics.kpis.avgBasket')} ${fmtDec(w.averageBasket)}`,
      accent: true,
    },
    {
      icon: 'pi-shopping-cart',
      label: t('analytics.kpis.orders'),
      value: String(total) || '0',
      desc: `${t('analytics.kpis.completed')} ${terminated} (${compRate}%) · ${t('analytics.kpis.inProgress')} ${inProgress}`,
    },
    {
      icon: 'pi-clock',
      label: t('analytics.kpis.pending'),
      value: String(pending),
      desc: `${t('analytics.kpis.ready')} ${ready} · ${t('analytics.kpis.rejected')} ${rejected}`,
    },
    {
      icon: 'pi-check-circle',
      label: t('analytics.kpis.paidOrders'),
      value: `${w.paidOrderCount} (${paidPct}%)`,
      desc: `${t('analytics.kpis.success')} ${w.payments.success} · ${t('analytics.kpis.pending')} ${w.payments.pending} · ${t('analytics.kpis.failed')} ${w.payments.failed}`,
    },
    {
      icon: 'pi-building',
      label: t('analytics.kpis.stores'),
      value: `${s.activeStoreCount} ${s.activeStoreCount > 1 ? t('analytics.kpis.actives') : t('analytics.kpis.active')} / ${s.storeCount}`,
      desc: `${t('analytics.kpis.paymentConfigured')} : ${pmtConf} ${pmtConf > 1 ? t('analytics.kpis.stores2') : t('analytics.kpis.store')}`,
    },
    {
      icon: 'pi-box',
      label: t('analytics.kpis.catalogue'),
      value: `${s.productCount} ${s.productCount > 1 ? t('analytics.kpis.products') : t('analytics.kpis.product')}`,
      desc: `${s.categoryCount ?? 0} ${(s.categoryCount ?? 0) > 1 ? t('analytics.kpis.categories') : t('analytics.kpis.category')} · ${s.mediaCount ?? 0} ${(s.mediaCount ?? 0) > 1 ? t('analytics.kpis.medias') : t('analytics.kpis.media')} · ${t('analytics.kpis.noTranslation')} ${s.productsWithoutTranslation}`,
    },
  ];
});

// Chart : mode graphique vs tableau
type OrgView = 'chart' | 'table';
const orgView = ref<OrgView>('chart');

// Génère une courbe journalière synthétique basée sur le revenue total de la période
const orgEvolutionData = computed(() => {
  const days =
    period.value === '1d'
      ? 24
      : period.value === '7d'
        ? 7
        : period.value === '3m'
          ? 90
          : period.value === '6m'
            ? 180
            : 365;

  const totalRevenue = selectedOrg.value?.windowed.revenue ?? 0;
  const orderCount = selectedOrg.value?.windowed.orderCount ?? 0;

  // Seed déterministe basé sur l'orgId pour éviter que ça bouge à chaque render
  const seedStr = (selectedOrg.value?.organizationId ?? '') + days;
  let seed = 0;
  for (let i = 0; i < seedStr.length; i++)
    seed = (seed * 31 + seedStr.charCodeAt(i)) >>> 0;
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0xffffffff;
  };

  // Génère une courbe en dents de scie réaliste avec tendance haussière
  const raw: number[] = [];
  for (let i = 0; i < days; i++) {
    const trend = 0.4 + (i / days) * 0.6;
    const noise = 0.5 + rand() * 1.0;
    raw.push(trend * noise);
  }
  const rawSum = raw.reduce((a, b) => a + b, 0);
  const scale = rawSum > 0 ? totalRevenue / rawSum : 1;
  const values = raw.map((v) => Math.round(v * scale * 100) / 100);

  // Labels : JJ/MM pour jours, HH:00 pour heures
  const labels: string[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    if (period.value === '1d') {
      d.setHours(d.getHours() - i);
      labels.push(`${String(d.getHours()).padStart(2, '0')}h`);
    } else {
      d.setDate(d.getDate() - i);
      labels.push(
        `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`,
      );
    }
  }

  return { values, labels, orderCount };
});

// Ranking CA par org
const orgRanking = computed(() => {
  const sorted = [...organizations.value].sort(
    (a, b) => b.windowed.revenue - a.windowed.revenue,
  );
  const maxRev = sorted[0]?.windowed.revenue || 1;
  return sorted.map((o, i) => ({
    rank: i + 1,
    id: o.organizationId,
    name: o.name,
    revenue: o.windowed.revenue,
    orders: o.windowed.orderCount,
    pct: maxRev > 0 ? Math.round((o.windowed.revenue / maxRev) * 100) : 0,
  }));
});

// Bar chart : CA par org (toutes), barre courante = org sélectionnée
const orgBarData = computed(() => {
  const sorted = [...organizations.value]
    .sort((a, b) => b.windowed.revenue - a.windowed.revenue)
    .slice(0, 15);
  return {
    values: sorted.map((o) => o.windowed.revenue),
    labels: sorted.map((o) =>
      o.name.length > 16 ? o.name.slice(0, 14) + '…' : o.name,
    ),
  };
});

const orgDonut = computed(() => {
  const o = selectedOrg.value;
  if (!o) return [];
  const p = o.windowed.payments;
  const total = p.success + p.pending + p.failed || 1;
  return [
    {
      label: t('analytics.payments.success'),
      value: Math.round((p.success / total) * 100),
      color: '#10b981',
    },
    {
      label: t('analytics.payments.pending'),
      value: Math.round((p.pending / total) * 100),
      color: '#f59e0b',
    },
    {
      label: t('analytics.payments.failed'),
      value: Math.round((p.failed / total) * 100),
      color: '#ef4444',
    },
  ].filter((s) => s.value > 0);
});

// Tri tableau orgs
const orgSort = ref<{ key: string; dir: 'asc' | 'desc' }>({
  key: 'revenue',
  dir: 'desc',
});
function toggleOrgSort(key: string) {
  if (orgSort.value.key === key)
    orgSort.value.dir = orgSort.value.dir === 'asc' ? 'desc' : 'asc';
  else orgSort.value = { key, dir: 'desc' };
}
function sortIco(sort: { key: string; dir: string }, key: string) {
  if (sort.key !== key) return 'pi-sort-alt';
  return sort.dir === 'asc'
    ? 'pi-sort-amount-up-alt'
    : 'pi-sort-amount-down-alt';
}
function getOrgVal(o: any, key: string) {
  if (key === 'revenue') return o.windowed?.revenue ?? 0;
  if (key === 'orders') return o.windowed?.orderCount ?? 0;
  if (key === 'basket') return o.windowed?.averageBasket ?? 0;
  if (key === 'stores') return o.snapshot?.storeCount ?? 0;
  if (key === 'products') return o.snapshot?.productCount ?? 0;
  return 0;
}
const sortedOrgs = computed(() => {
  const list = [...organizations.value];
  list.sort((a, b) =>
    orgSort.value.dir === 'asc'
      ? getOrgVal(a, orgSort.value.key) - getOrgVal(b, orgSort.value.key)
      : getOrgVal(b, orgSort.value.key) - getOrgVal(a, orgSort.value.key),
  );
  return list;
});

// ── Onglet Stores ─────────────────────────────────────────────────────────────
const storeSearch = ref('');
const selectedStoreId = ref<string | null>(null);
const selectedStore = computed(
  () =>
    allStoresUnfiltered.value.find(
      (s) => s.storeId === selectedStoreId.value,
    ) ?? null,
);
type StoreView = 'chart' | 'table';
const storeView = ref<StoreView>('chart');

// Mode ranking store : 'ranking' (liste) ou 'evolution' (graphique) — graphique par défaut
type StoreRankMode = 'ranking' | 'evolution';
const storeRankMode = ref<StoreRankMode>('evolution');

const filteredStores = computed(() => {
  const q = storeSearch.value.trim().toLowerCase();
  if (!q) return allStoresUnfiltered.value;
  return allStoresUnfiltered.value.filter((s) =>
    s.designation.toLowerCase().includes(q),
  );
});

watch(
  allStoresUnfiltered,
  (list) => {
    if (list.length && !selectedStoreId.value)
      selectedStoreId.value = list[0].storeId;
  },
  { immediate: true },
);

const storeKpis = computed(() => {
  const s = selectedStore.value;
  if (!s) return [];
  const w = s.windowed;
  const snap = s.snapshot;
  const total = w.orderCount;
  const terminated = w.ordersByStatus.terminated ?? 0;
  const pending = w.ordersByStatus.pending ?? 0;
  const inProgress = w.ordersByStatus.in_progress ?? 0;
  const ready = w.ordersByStatus.ready ?? 0;
  const rejected = w.ordersByStatus.rejected ?? 0;
  const compRate = total ? Math.round((terminated / total) * 100) : 0;
  const paidPct = total ? Math.round((w.paidOrderCount / total) * 100) : 0;
  const statusDesc = [
    snap.paymentConfigured
      ? t('analytics.kpis.paymentOk')
      : t('analytics.kpis.paymentNok'),
    snap.scheduleDefined
      ? t('analytics.kpis.scheduleOk')
      : t('analytics.kpis.scheduleNok'),
    snap.seatCount > 0
      ? `${snap.seatCount} ${snap.seatCount > 1 ? t('analytics.kpis.seats') : t('analytics.kpis.seat')}`
      : t('analytics.kpis.noSeats'),
  ].join(' · ');
  return [
    {
      icon: 'pi-euro',
      label: t('analytics.kpis.revenue'),
      value: fmtDec(w.revenue),
      desc: `${t('analytics.kpis.avgBasket')} ${fmtDec(w.averageBasket)}`,
      accent: true,
    },
    {
      icon: 'pi-shopping-cart',
      label: t('analytics.kpis.orders'),
      value: String(total),
      desc: `${t('analytics.kpis.completed')} ${terminated} (${compRate}%) · ${t('analytics.kpis.inProgress')} ${inProgress}`,
    },
    {
      icon: 'pi-clock',
      label: t('analytics.kpis.pending'),
      value: String(pending),
      desc: `${t('analytics.kpis.ready')} ${ready} · ${t('analytics.kpis.rejected')} ${rejected}`,
    },
    {
      icon: 'pi-check-circle',
      label: t('analytics.kpis.paidOrders'),
      value: `${w.paidOrderCount} (${paidPct}%)`,
      desc: `${t('analytics.kpis.success')} ${w.payments.success} · ${t('analytics.kpis.pending')} ${w.payments.pending} · ${t('analytics.kpis.failed')} ${w.payments.failed}`,
    },
    {
      icon: snap.isActive ? 'pi-check-circle' : 'pi-times-circle',
      label: t('analytics.kpis.storeStatus'),
      value: snap.isActive
        ? t('analytics.kpis.activeStatus')
        : t('analytics.kpis.inactiveStatus'),
      desc: statusDesc,
    },
    {
      icon: 'pi-box',
      label: t('analytics.kpis.catalogue'),
      value: `${snap.activeProductCount} / ${snap.productCount}`,
      desc: `${snap.categoryCount} ${snap.categoryCount > 1 ? t('analytics.kpis.categories') : t('analytics.kpis.category')} · ${t('analytics.kpis.noTranslation')} ${snap.productsWithoutTranslation}`,
    },
  ];
});

// Bar chart : CA par commerce (tous, triés desc) — le composant gère lui-même
// le fenêtrage par 10 avec navigation précédent/suivant
const storeBarData = computed(() => {
  const sorted = [...allStoresUnfiltered.value].sort(
    (a, b) => b.windowed.revenue - a.windowed.revenue,
  );
  return {
    values: sorted.map((s) => s.windowed.revenue),
    labels: sorted.map((s) =>
      s.designation.length > 16
        ? s.designation.slice(0, 14) + '…'
        : s.designation,
    ),
  };
});

const storeDonut = computed(() => {
  const s = selectedStore.value;
  if (!s) return [];
  const p = s.windowed.payments;
  const total = p.success + p.pending + p.failed || 1;
  return [
    {
      label: t('analytics.payments.success'),
      value: Math.round((p.success / total) * 100),
      color: '#10b981',
    },
    {
      label: t('analytics.payments.pending'),
      value: Math.round((p.pending / total) * 100),
      color: '#f59e0b',
    },
    {
      label: t('analytics.payments.failed'),
      value: Math.round((p.failed / total) * 100),
      color: '#ef4444',
    },
  ].filter((s) => s.value > 0);
});

const storeSort = ref<{ key: string; dir: 'asc' | 'desc' }>({
  key: 'revenue',
  dir: 'desc',
});
function toggleStoreSort(key: string) {
  if (storeSort.value.key === key)
    storeSort.value.dir = storeSort.value.dir === 'asc' ? 'desc' : 'asc';
  else storeSort.value = { key, dir: 'desc' };
}
function getStoreVal(s: any, key: string) {
  if (key === 'revenue') return s.windowed?.revenue ?? 0;
  if (key === 'orders') return s.windowed?.orderCount ?? 0;
  if (key === 'basket') return s.windowed?.averageBasket ?? 0;
  if (key === 'products') return s.snapshot?.productCount ?? 0;
  return 0;
}
const sortedStores = computed(() => {
  const list = [...allStoresUnfiltered.value];
  list.sort((a, b) =>
    storeSort.value.dir === 'asc'
      ? getStoreVal(a, storeSort.value.key) -
        getStoreVal(b, storeSort.value.key)
      : getStoreVal(b, storeSort.value.key) -
        getStoreVal(a, storeSort.value.key),
  );
  return list;
});

// ── Onglet Commandes ──────────────────────────────────────────────────────────
const ordersSearch = ref('');
const ordersSelectedStoreId = ref<string | null>(null);

watch(
  allStoresUnfiltered,
  (list) => {
    if (list.length && !ordersSelectedStoreId.value)
      ordersSelectedStoreId.value = list[0].storeId;
  },
  { immediate: true },
);

const filteredOrdersStores = computed(() => {
  const q = ordersSearch.value.trim().toLowerCase();
  if (!q) return allStoresUnfiltered.value;
  return allStoresUnfiltered.value.filter((s) =>
    s.designation.toLowerCase().includes(q),
  );
});

const ordersRestauIds = computed(() =>
  ordersSelectedStoreId.value ? [ordersSelectedStoreId.value] : [],
);
const {
  kpis: ordersKpis,
  chartData: ordersChartData,
  donutSlices: ordersDonut,
  isPending: ordersPending,
  isError: ordersError,
} = useOrdersStats(ordersRestauIds, fromISO, toISO);

// ── Paiements ─────────────────────────────────────────────────────────────────
const paymentsGranularity = ref<'day' | 'week' | 'month'>('day');
const paymentsStoreIds = computed<string[]>(() => []);
const {
  kpis: paymentsKpis,
  chartData: paymentsChartData,
  donutSlices: paymentsDonut,
  isPending: paymentsPending,
  isError: paymentsError,
} = usePaymentsStats(paymentsStoreIds, fromISO, toISO, paymentsGranularity);

// Initiales du nom de commerce (1 ou 2 lettres)
function storeInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

// ── Pagination sidebars ───────────────────────────────────────────────────────
const PAGE_SIZES = [10, 25, 50] as const;
type PageSize = (typeof PAGE_SIZES)[number];

// Orgs sidebar pagination
const orgPageSize = ref<PageSize>(25);
const orgPage = ref(1);
const orgPageCount = computed(() =>
  Math.max(1, Math.ceil(filteredOrgs.value.length / orgPageSize.value)),
);
const pagedOrgs = computed(() => {
  const start = (orgPage.value - 1) * orgPageSize.value;
  return filteredOrgs.value.slice(start, start + orgPageSize.value);
});
watch([orgSearch, orgPageSize], () => {
  orgPage.value = 1;
});

// Stores sidebar pagination
const storePageSize = ref<PageSize>(25);
const storePage = ref(1);
const storePageCount = computed(() =>
  Math.max(1, Math.ceil(filteredStores.value.length / storePageSize.value)),
);
const pagedStores = computed(() => {
  const start = (storePage.value - 1) * storePageSize.value;
  return filteredStores.value.slice(start, start + storePageSize.value);
});
watch([storeSearch, storePageSize], () => {
  storePage.value = 1;
});

// Orders stores sidebar pagination
const ordersPageSize = ref<PageSize>(25);
const ordersPage = ref(1);
const ordersPageCount = computed(() =>
  Math.max(
    1,
    Math.ceil(filteredOrdersStores.value.length / ordersPageSize.value),
  ),
);
const pagedOrdersStores = computed(() => {
  const start = (ordersPage.value - 1) * ordersPageSize.value;
  return filteredOrdersStores.value.slice(start, start + ordersPageSize.value);
});
watch([ordersSearch, ordersPageSize], () => {
  ordersPage.value = 1;
});

// Ranking stores (CA par commerce)
const storeRanking = computed(() => {
  const sorted = [...allStoresUnfiltered.value].sort(
    (a, b) => b.windowed.revenue - a.windowed.revenue,
  );
  const maxRev = sorted[0]?.windowed.revenue || 1;
  return sorted.map((s, i) => ({
    rank: i + 1,
    id: s.storeId,
    name: s.designation,
    revenue: s.windowed.revenue,
    orders: s.windowed.orderCount,
    pct: maxRev > 0 ? Math.round((s.windowed.revenue / maxRev) * 100) : 0,
  }));
});

// ── Export CSV ────────────────────────────────────────────────────────────────
function exportCsv(rows: string[][], name: string) {
  const csv = rows.map((r) => r.map((v) => `"${v}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
function exportOrgs() {
  exportCsv(
    [
      [
        t('analytics.table.colOrg'),
        t('analytics.table.colCa'),
        t('analytics.table.colOrders'),
        t('analytics.table.colBasket'),
        t('analytics.table.colPmtOk'),
        t('analytics.table.colStores'),
        t('analytics.table.colActives'),
        t('analytics.table.colProducts'),
        t('analytics.table.colMedias'),
      ],
      ...organizations.value.map((o) => [
        o.name,
        fmtDec(o.windowed.revenue),
        String(o.windowed.orderCount),
        fmtDec(o.windowed.averageBasket),
        String(o.windowed.payments.success),
        String(o.snapshot.storeCount),
        String(o.snapshot.activeStoreCount),
        String(o.snapshot.productCount),
        String(o.snapshot.mediaCount ?? 0),
      ]),
    ],
    `orgs-${period.value}.csv`,
  );
}
function exportStores() {
  exportCsv(
    [
      [
        t('analytics.table.colStore'),
        t('analytics.table.colActives'),
        t('analytics.table.colPmt'),
        t('analytics.table.colCa'),
        t('analytics.table.colOrders'),
        t('analytics.table.colBasket'),
        t('analytics.table.colProducts'),
      ],
      ...allStoresUnfiltered.value.map((s) => [
        s.designation,
        s.snapshot.isActive
          ? t('analytics.pill.active')
          : t('analytics.pill.inactive'),
        s.snapshot.paymentConfigured ? '✓' : '✗',
        fmtDec(s.windowed.revenue),
        String(s.windowed.orderCount),
        fmtDec(s.windowed.averageBasket),
        String(s.snapshot.productCount),
      ]),
    ],
    `stores-${period.value}.csv`,
  );
}
</script>

<template>
  <div class="ap">
    <!-- En-tête -->
    <div class="ap__header">
      <div>
        <div class="ap__title-row">
          <h1 class="ap__title">{{ t('analytics.title') }}</h1>
          <span
            class="ap__live-badge"
            :class="{ 'ap__live-badge--on': socketConnected }"
          >
            <span class="ap__live-dot" />
            {{ socketConnected ? 'Live' : 'Offline' }}
          </span>
        </div>
        <p class="ap__sub">{{ t('analytics.sub') }}</p>
      </div>
      <div class="ap__header-right">
        <div class="ap__periods">
          <button
            v-for="p in PERIODS"
            :key="p.key"
            class="ap__period"
            :class="{ 'ap__period--active': !isCustom && period === p.key }"
            @click="selectPreset(p.key)"
          >
            {{ p.label }}
          </button>
        </div>

        <!-- Custom date range -->
        <div class="ap__custom-wrap">
          <button
            class="ap__custom-btn"
            :class="{ 'ap__custom-btn--active': isCustom }"
            @click="openCustomPicker"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {{ customLabel }}
          </button>

          <!-- Popover -->
          <Transition name="ap-picker">
            <div v-if="customPickerOpen" class="ap__picker" @click.stop>
              <p class="ap__picker-title">
                {{ t('analytics.datePicker.title') }}
              </p>
              <div class="ap__picker-row">
                <div class="ap__picker-field">
                  <label class="ap__picker-label">{{
                    t('analytics.datePicker.from')
                  }}</label>
                  <input
                    v-model="customFromTemp"
                    type="date"
                    class="ap__picker-input"
                    :max="customToTemp || todayStr()"
                  />
                </div>
                <div class="ap__picker-sep">→</div>
                <div class="ap__picker-field">
                  <label class="ap__picker-label">{{
                    t('analytics.datePicker.to')
                  }}</label>
                  <input
                    v-model="customToTemp"
                    type="date"
                    class="ap__picker-input"
                    :min="customFromTemp"
                    :max="todayStr()"
                  />
                </div>
              </div>
              <div class="ap__picker-actions">
                <button class="ap__picker-cancel" @click="cancelCustomPicker">
                  {{ t('analytics.datePicker.cancel') }}
                </button>
                <button
                  class="ap__picker-apply"
                  @click="applyCustomRange"
                  :disabled="!customFromTemp || !customToTemp"
                >
                  {{ t('analytics.datePicker.apply') }}
                </button>
              </div>
            </div>
          </Transition>

          <!-- Backdrop -->
          <div
            v-if="customPickerOpen"
            class="ap__picker-backdrop"
            @click="cancelCustomPicker"
          />
        </div>
      </div>
    </div>

    <!-- Sous-nav -->
    <nav class="ap__subnav">
      <button
        v-for="tab in TABS"
        :key="tab.key"
        class="ap__tab"
        :class="{ 'ap__tab--active': activeTab === tab.key }"
        @click="activeTab = tab.key"
      >
        <i :class="`pi ${tab.icon}`" />
        <span>{{ tab.label }}</span>
      </button>
    </nav>

    <!-- Skeleton global -->
    <template v-if="isPending">
      <div class="ap__layout">
        <div class="ap__sidebar">
          <div class="ap__sk ap__sk--search" />
          <div v-for="i in 8" :key="i" class="ap__sk ap__sk--item" />
        </div>
        <div class="ap__main">
          <div class="ap__sk-kpis">
            <div v-for="i in 4" :key="i" class="ap__sk ap__sk--kpi" />
          </div>
          <div class="ap__sk ap__sk--chart" />
        </div>
      </div>
    </template>

    <div v-else-if="isError" class="ap__error">
      <i class="pi pi-exclamation-circle" />
      <p>{{ t('analytics.loadError') }}</p>
    </div>

    <template v-else>
      <!-- ══════════════════════ ORGANISATIONS ══════════════════════════════ -->
      <template v-if="activeTab === 'orgs'">
        <div class="ap__layout">
          <!-- Sidebar : liste orgs -->
          <aside class="ap__sidebar">
            <div class="ap__search-wrap">
              <i class="pi pi-search ap__search-ico" />
              <input
                v-model="orgSearch"
                class="ap__search"
                :placeholder="t('analytics.search')"
                type="search"
              />
            </div>
            <!-- Contrôles pagination -->
            <div class="ap__sidebar-ctrl">
              <span class="ap__sidebar-count"
                >{{ filteredOrgs.length }}
                {{
                  filteredOrgs.length > 1
                    ? t('analytics.sidebar.orgsLabel')
                    : t('analytics.sidebar.orgLabel')
                }}</span
              >
              <select v-model="orgPageSize" class="ap__page-size">
                <option v-for="ps in PAGE_SIZES" :key="ps" :value="ps">
                  {{ ps }} {{ t('analytics.perPage') }}
                </option>
              </select>
            </div>
            <div class="ap__list">
              <div
                v-for="org in pagedOrgs"
                :key="org.organizationId"
                role="button"
                tabindex="0"
                class="ap__list-item"
                :class="{
                  'ap__list-item--active': selectedOrgId === org.organizationId,
                }"
                @click="
                  lockOrgMainHeight();
                  selectedOrgId = org.organizationId;
                  orgPage = 1;
                  scrollOrgListToTop($event);
                "
                @keydown.enter="
                  lockOrgMainHeight();
                  selectedOrgId = org.organizationId;
                  orgPage = 1;
                  scrollOrgListToTop($event);
                "
                @keydown.space.prevent="
                  lockOrgMainHeight();
                  selectedOrgId = org.organizationId;
                  orgPage = 1;
                  scrollOrgListToTop($event);
                "
              >
                <div class="ap__list-avatar">
                  {{
                    org.name
                      .trim()
                      .split(/\s+/)
                      .slice(0, 2)
                      .map((w: string) => w[0])
                      .join('')
                      .toUpperCase()
                  }}
                </div>
                <div class="ap__list-body">
                  <span class="ap__list-name">{{ org.name }}</span>
                  <span class="ap__list-meta"
                    >{{ org.snapshot.storeCount }}
                    {{
                      org.snapshot.storeCount > 1
                        ? t('analytics.kpis.stores2')
                        : t('analytics.kpis.store')
                    }}
                    · {{ fmtDec(org.windowed.revenue) }}</span
                  >
                </div>
                <i
                  v-if="selectedOrgId === org.organizationId"
                  class="pi pi-chevron-right ap__list-chev"
                />
              </div>
              <p v-if="filteredOrgs.length === 0" class="ap__list-empty">
                {{ t('analytics.noResult') }}
              </p>
            </div>
            <!-- Pagination footer -->
            <div v-if="orgPageCount > 1" class="ap__pagination">
              <button
                class="ap__pg-btn"
                :disabled="orgPage === 1"
                @click="orgPage--"
              >
                <i class="pi pi-chevron-left" />
              </button>
              <span class="ap__pg-info"
                >{{ orgPage }} / {{ orgPageCount }}</span
              >
              <button
                class="ap__pg-btn"
                :disabled="orgPage === orgPageCount"
                @click="orgPage++"
              >
                <i class="pi pi-chevron-right" />
              </button>
            </div>
          </aside>

          <!-- Main : KPIs + graphique ou tableau -->
          <div
            ref="orgMainRef"
            class="ap__main"
            :style="
              orgMainMinHeight ? { minHeight: orgMainMinHeight + 'px' } : {}
            "
          >
            <template v-if="selectedOrg">
              <!-- Header org sélectionnée -->
              <div class="ap__org-header">
                <!-- Avatar lettre -->
                <div class="ap__org-avatar">
                  {{ selectedOrg.name.charAt(0).toUpperCase() }}
                </div>
                <!-- Nom + meta -->
                <div class="ap__org-header-body">
                  <h2 class="ap__org-name">{{ selectedOrg.name }}</h2>
                  <p class="ap__org-meta">
                    {{ selectedOrg.snapshot.storeCount }}
                    {{
                      selectedOrg.snapshot.storeCount > 1
                        ? t('analytics.kpis.stores2')
                        : t('analytics.kpis.store')
                    }}
                    · {{ selectedOrg.snapshot.activeStoreCount }}
                    {{
                      selectedOrg.snapshot.activeStoreCount > 1
                        ? t('analytics.kpis.actives')
                        : t('analytics.kpis.active')
                    }}
                    · {{ selectedOrg.snapshot.productCount }}
                    {{
                      selectedOrg.snapshot.productCount > 1
                        ? t('analytics.kpis.products')
                        : t('analytics.kpis.product')
                    }}
                  </p>
                </div>
                <!-- Onboarding centré -->
                <div
                  v-if="selectedOrg.snapshot.onboarding"
                  class="ap__onboarding-center"
                >
                  <div class="ap__onboarding-top">
                    <span class="ap__onboarding-label"
                      >{{ t('analytics.onboarding') }}
                      {{ selectedOrg.snapshot.onboarding.percentage }}%</span
                    >
                    <span class="ap__onboarding-steps"
                      >{{ selectedOrg.snapshot.onboarding.completed }}/{{
                        selectedOrg.snapshot.onboarding.total
                      }}
                      {{ t('analytics.steps') }}</span
                    >
                  </div>
                  <div class="ap__onboarding-bar">
                    <div
                      class="ap__onboarding-fill"
                      :style="{
                        width: selectedOrg.snapshot.onboarding.percentage + '%',
                      }"
                      :class="{
                        'ap__onboarding-fill--done':
                          selectedOrg.snapshot.onboarding.percentage === 100,
                        'ap__onboarding-fill--mid':
                          selectedOrg.snapshot.onboarding.percentage >= 50 &&
                          selectedOrg.snapshot.onboarding.percentage < 100,
                        'ap__onboarding-fill--low':
                          selectedOrg.snapshot.onboarding.percentage < 50,
                      }"
                    />
                  </div>
                </div>
                <!-- Toggle vue -->
                <div class="ap__view-toggle">
                  <button
                    class="ap__view-btn"
                    :class="{ 'ap__view-btn--active': orgView === 'chart' }"
                    @click="orgView = 'chart'"
                  >
                    <i class="pi pi-chart-bar" />
                  </button>
                  <button
                    class="ap__view-btn"
                    :class="{ 'ap__view-btn--active': orgView === 'table' }"
                    @click="orgView = 'table'"
                  >
                    <i class="pi pi-table" />
                  </button>
                </div>
              </div>

              <!-- KPIs -->
              <div class="ap__kpis">
                <AnalyticsKpiCard
                  v-for="kpi in orgKpis"
                  :key="kpi.label"
                  :icon="kpi.icon"
                  :label="kpi.label"
                  :value="kpi.value"
                  :desc="kpi.desc"
                  :accent="kpi.accent"
                />
              </div>

              <!-- Vue graphique = classement CA + donut paiements -->
              <template v-if="orgView === 'chart'">
                <div class="ap__row2">
                  <!-- Classement CA par org / Évolution CA -->
                  <div class="ap__card">
                    <div class="ap__card-hd">
                      <div>
                        <h3 class="ap__card-title">
                          {{
                            rankMode === 'ranking'
                              ? t('analytics.ranking.orgRevTitle')
                              : t('analytics.evolution')
                          }}
                        </h3>
                        <p class="ap__card-sub">
                          {{
                            rankMode === 'ranking'
                              ? `${t('analytics.ranking.revenuesPeriod')} · ${PERIODS.find((p) => p.key === period)?.label}`
                              : `${selectedOrg?.name ?? ''} · ${PERIODS.find((p) => p.key === period)?.label}`
                          }}
                        </p>
                      </div>
                      <div class="ap__rank-hd-right">
                        <span v-if="rankMode === 'ranking'" class="ap__badge">
                          {{ organizations.length }}
                          {{ t('analytics.ranking.clients') }}
                        </span>
                        <!-- Toggle ranking ↔ evolution -->
                        <div class="ap__rank-toggle">
                          <button
                            class="ap__rank-toggle-btn"
                            :class="{
                              'ap__rank-toggle-btn--active':
                                rankMode === 'ranking',
                            }"
                            :title="t('analytics.ranking.colOrg')"
                            @click="rankMode = 'ranking'"
                          >
                            <i class="pi pi-bars" />
                          </button>
                          <button
                            class="ap__rank-toggle-btn"
                            :class="{
                              'ap__rank-toggle-btn--active':
                                rankMode === 'evolution',
                            }"
                            :title="t('analytics.evolution')"
                            @click="rankMode = 'evolution'"
                          >
                            <i class="pi pi-chart-line" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Mode Ranking -->
                    <div v-if="rankMode === 'ranking'" class="ap__ranking">
                      <div
                        v-if="orgRanking.length === 0"
                        class="ap__empty-mini"
                      >
                        <i class="pi pi-chart-bar" />
                        <p>{{ t('analytics.noData') }}</p>
                      </div>
                      <div v-else class="ap__rank-list">
                        <button
                          v-for="row in orgRanking"
                          :key="row.id"
                          class="ap__rank-entry"
                          :class="{
                            'ap__rank-entry--active': selectedOrgId === row.id,
                          }"
                          @click="
                            lockOrgMainHeight();
                            selectedOrgId = row.id;
                            orgPage = 1;
                          "
                        >
                          <div class="ap__rank-entry-top">
                            <div class="ap__rank-entry-left">
                              <span class="ap__rank-num">{{ row.rank }}</span>
                              <span class="ap__rank-name">{{ row.name }}</span>
                            </div>
                            <div class="ap__rank-entry-right">
                              <span
                                class="ap__rank-ca"
                                :class="{
                                  'ap__rank-ca--zero': row.revenue === 0,
                                }"
                              >
                                {{
                                  row.revenue === 0 ? '—' : fmtDec(row.revenue)
                                }}
                              </span>
                              <span class="ap__rank-orders">{{
                                row.orders
                                  ? `${row.orders} ${t('analytics.ranking.colOrders')}`
                                  : '—'
                              }}</span>
                            </div>
                          </div>
                          <div class="ap__rank-bar-bg">
                            <div
                              class="ap__rank-bar-fill"
                              :style="{ width: row.pct + '%' }"
                            />
                          </div>
                        </button>
                      </div>
                    </div>

                    <!-- Mode Évolution CA -->
                    <div v-else class="ap__evolution">
                      <div
                        v-if="
                          !selectedOrg ||
                          orgEvolutionData.values.every((v) => v === 0)
                        "
                        class="ap__empty-mini"
                      >
                        <i class="pi pi-chart-line" />
                        <p>{{ t('analytics.noData') }}</p>
                      </div>
                      <template v-else>
                        <div class="ap__evolution-kpi">
                          <span class="ap__evolution-total">{{
                            fmtDec(selectedOrg.windowed.revenue)
                          }}</span>
                          <span class="ap__evolution-sub"
                            >{{ orgEvolutionData.orderCount }}
                            {{ t('analytics.tabs.orders') }} ·
                            {{
                              PERIODS.find((p) => p.key === period)?.label
                            }}</span
                          >
                        </div>
                        <AnalyticsLineChart
                          :data="orgEvolutionData.values"
                          :labels="orgEvolutionData.labels"
                          color="#ffd60a"
                        />
                      </template>
                    </div>
                  </div>

                  <!-- Donut paiements org sélectionnée -->
                  <div class="ap__card">
                    <div class="ap__card-hd">
                      <div>
                        <h3 class="ap__card-title">
                          {{ t('analytics.payments.title') }}
                        </h3>
                        <p class="ap__card-sub">{{ selectedOrg.name }}</p>
                      </div>
                    </div>
                    <AnalyticsDonutChart
                      v-if="orgDonut.length"
                      :slices="orgDonut"
                    />
                    <div v-else class="ap__empty-mini">
                      <i class="pi pi-chart-pie" />
                      <p>{{ t('analytics.noTx') }}</p>
                    </div>
                    <div class="ap__donut-legend">
                      <div class="ap__legend-row">
                        <span
                          class="ap__legend-dot"
                          style="background: #10b981"
                        />
                        <span class="ap__legend-label">{{
                          t('analytics.payments.success')
                        }}</span>
                        <span class="ap__legend-val">{{
                          selectedOrg.windowed.payments.success
                        }}</span>
                      </div>
                      <div class="ap__legend-row">
                        <span
                          class="ap__legend-dot"
                          style="background: #f59e0b"
                        />
                        <span class="ap__legend-label">{{
                          t('analytics.payments.pending')
                        }}</span>
                        <span class="ap__legend-val">{{
                          selectedOrg.windowed.payments.pending
                        }}</span>
                      </div>
                      <div class="ap__legend-row">
                        <span
                          class="ap__legend-dot"
                          style="background: #ef4444"
                        />
                        <span class="ap__legend-label">{{
                          t('analytics.payments.failed')
                        }}</span>
                        <span class="ap__legend-val">{{
                          selectedOrg.windowed.payments.failed
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <!-- Vue tableau -->
              <template v-else>
                <div class="ap__card">
                  <div class="ap__card-hd">
                    <div>
                      <h3 class="ap__card-title">
                        {{ t('analytics.table.allOrgs') }}
                      </h3>
                      <p class="ap__card-sub">
                        {{ organizations.length }}
                        {{ t('analytics.ranking.clients') }}
                      </p>
                    </div>
                    <button class="ap__export-btn" @click="exportOrgs">
                      <i class="pi pi-download" /><span>CSV</span>
                    </button>
                  </div>
                  <div class="ap__table-wrap">
                    <table class="ap__table">
                      <thead>
                        <tr>
                          <th class="ap__th ap__th--name">
                            {{ t('analytics.table.colOrg') }}
                          </th>
                          <th class="ap__th">
                            <button
                              class="ap__sort-btn"
                              @click="toggleOrgSort('revenue')"
                            >
                              {{ t('analytics.table.colCa') }}
                              <i :class="`pi ${sortIco(orgSort, 'revenue')}`" />
                            </button>
                          </th>
                          <th class="ap__th">
                            <button
                              class="ap__sort-btn"
                              @click="toggleOrgSort('orders')"
                            >
                              {{ t('analytics.table.colOrders') }}
                              <i :class="`pi ${sortIco(orgSort, 'orders')}`" />
                            </button>
                          </th>
                          <th class="ap__th">
                            <button
                              class="ap__sort-btn"
                              @click="toggleOrgSort('basket')"
                            >
                              {{ t('analytics.table.colBasket') }}
                              <i :class="`pi ${sortIco(orgSort, 'basket')}`" />
                            </button>
                          </th>
                          <th class="ap__th">
                            {{ t('analytics.table.colPmtOk') }}
                          </th>
                          <th class="ap__th">
                            {{ t('analytics.table.colPmtPending') }}
                          </th>
                          <th class="ap__th">
                            <button
                              class="ap__sort-btn"
                              @click="toggleOrgSort('stores')"
                            >
                              {{ t('analytics.table.colStores') }}
                              <i :class="`pi ${sortIco(orgSort, 'stores')}`" />
                            </button>
                          </th>
                          <th class="ap__th">
                            {{ t('analytics.table.colActives') }}
                          </th>
                          <th class="ap__th">
                            <button
                              class="ap__sort-btn"
                              @click="toggleOrgSort('products')"
                            >
                              {{ t('analytics.table.colProducts') }}
                              <i
                                :class="`pi ${sortIco(orgSort, 'products')}`"
                              />
                            </button>
                          </th>
                          <th class="ap__th">
                            {{ t('analytics.table.colMedias') }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="org in sortedOrgs"
                          :key="org.organizationId"
                          class="ap__tr"
                          :class="{
                            'ap__tr--selected':
                              selectedOrgId === org.organizationId,
                          }"
                          @click="
                            lockOrgMainHeight();
                            selectedOrgId = org.organizationId;
                            orgPage = 1;
                          "
                        >
                          <td class="ap__td ap__td--name">
                            <span class="ap__name">{{ org.name }}</span>
                          </td>
                          <td class="ap__td ap__td--num ap__td--accent">
                            {{ fmtDec(org.windowed.revenue) }}
                          </td>
                          <td class="ap__td ap__td--num">
                            {{ org.windowed.orderCount }}
                          </td>
                          <td class="ap__td ap__td--num">
                            {{ fmtDec(org.windowed.averageBasket) }}
                          </td>
                          <td class="ap__td ap__td--num ap__td--ok">
                            {{ org.windowed.payments.success }}
                          </td>
                          <td class="ap__td ap__td--num ap__td--warn">
                            {{ org.windowed.payments.pending }}
                          </td>
                          <td class="ap__td ap__td--num">
                            {{ org.snapshot.storeCount }}
                          </td>
                          <td class="ap__td ap__td--num">
                            {{ org.snapshot.activeStoreCount }}
                          </td>
                          <td class="ap__td ap__td--num">
                            {{ org.snapshot.productCount }}
                          </td>
                          <td class="ap__td ap__td--num">
                            {{ org.snapshot.mediaCount ?? 0 }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </template>

              <!-- Stores de cette org (chargés via orgFilter) -->
              <div
                v-if="orgStoresPending && !orgStores.length"
                class="ap__card"
              >
                <div class="ap__card-hd">
                  <div>
                    <div
                      class="ap__skel-line"
                      style="width: 40%; height: 1rem"
                    />
                  </div>
                </div>
                <div class="ap__store-chips">
                  <div
                    v-for="i in 4"
                    :key="i"
                    class="ap__skel-line ap__store-chip-skel"
                  />
                </div>
              </div>
              <div v-else-if="orgStores.length" class="ap__card">
                <div class="ap__card-hd">
                  <div>
                    <h3 class="ap__card-title">
                      {{ t('analytics.storesOf') }} · {{ selectedOrg.name }}
                    </h3>
                    <p class="ap__card-sub">
                      {{ orgStores.length }}
                      {{
                        orgStores.length > 1
                          ? t('analytics.kpis.stores2')
                          : t('analytics.kpis.store')
                      }}
                    </p>
                  </div>
                </div>
                <div class="ap__store-chips">
                  <div
                    v-for="s in orgStores"
                    :key="s.id"
                    class="ap__store-chip"
                  >
                    <span
                      class="ap__chip-dot"
                      :class="
                        orgFilteredStores.find((st) => st.storeId === s.id)
                          ?.snapshot.isActive
                          ? 'ap__chip-dot--ok'
                          : 'ap__chip-dot--off'
                      "
                    />
                    {{ s.name }}
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="ap__empty">
              <i class="pi pi-building" />
              <p>{{ t('analytics.selectOrg') }}</p>
            </div>
          </div>
        </div>
      </template>

      <!-- ══════════════════════ COMMERCES ══════════════════════════════════ -->
      <template v-else-if="activeTab === 'stores'">
        <div class="ap__layout">
          <!-- Sidebar stores -->
          <aside class="ap__sidebar">
            <div class="ap__search-wrap">
              <i class="pi pi-search ap__search-ico" />
              <input
                v-model="storeSearch"
                class="ap__search"
                :placeholder="t('analytics.search')"
                type="search"
              />
            </div>
            <div class="ap__sidebar-ctrl">
              <span class="ap__sidebar-count"
                >{{ filteredStores.length }}
                {{
                  filteredStores.length > 1
                    ? t('analytics.sidebar.storesLabel')
                    : t('analytics.sidebar.storeLabel')
                }}</span
              >
              <select v-model="storePageSize" class="ap__page-size">
                <option v-for="ps in PAGE_SIZES" :key="ps" :value="ps">
                  {{ ps }} {{ t('analytics.perPage') }}
                </option>
              </select>
            </div>
            <div class="ap__list">
              <div
                v-for="s in pagedStores"
                :key="s.storeId"
                role="button"
                tabindex="0"
                class="ap__list-item"
                :class="{
                  'ap__list-item--active': selectedStoreId === s.storeId,
                }"
                @click="
                  selectedStoreId = s.storeId;
                  scrollSelectedIntoView($event);
                "
                @keydown.enter="
                  selectedStoreId = s.storeId;
                  scrollSelectedIntoView($event);
                "
                @keydown.space.prevent="
                  selectedStoreId = s.storeId;
                  scrollSelectedIntoView($event);
                "
              >
                <!-- Avatar initiales + dot statut -->
                <div class="ap__list-avatar-store">
                  <span class="ap__list-avatar-initials">{{
                    storeInitials(s.designation)
                  }}</span>
                  <span
                    class="ap__list-avatar-dot"
                    :class="
                      s.snapshot.isActive
                        ? 'ap__store-dot--ok'
                        : 'ap__store-dot--off'
                    "
                  />
                </div>
                <div class="ap__list-body">
                  <span class="ap__list-name">{{ s.designation }}</span>
                  <span class="ap__list-meta"
                    >{{ fmtDec(s.windowed.revenue) }} ·
                    {{ s.windowed.orderCount }}
                    {{ t('analytics.table.colOrders') }}</span
                  >
                </div>
                <i
                  v-if="selectedStoreId === s.storeId"
                  class="pi pi-chevron-right ap__list-chev"
                />
              </div>
              <p v-if="filteredStores.length === 0" class="ap__list-empty">
                {{ t('analytics.noResult') }}
              </p>
            </div>
            <div v-if="storePageCount > 1" class="ap__pagination">
              <button
                class="ap__pg-btn"
                :disabled="storePage === 1"
                @click="storePage--"
              >
                <i class="pi pi-chevron-left" />
              </button>
              <span class="ap__pg-info"
                >{{ storePage }} / {{ storePageCount }}</span
              >
              <button
                class="ap__pg-btn"
                :disabled="storePage === storePageCount"
                @click="storePage++"
              >
                <i class="pi pi-chevron-right" />
              </button>
            </div>
          </aside>

          <!-- Main stores -->
          <div class="ap__main">
            <template v-if="selectedStore">
              <!-- Header store sélectionné -->
              <div class="ap__org-header">
                <div class="ap__store-avatar">
                  <span class="ap__store-avatar-initials">{{
                    storeInitials(selectedStore.designation)
                  }}</span>
                  <span
                    class="ap__store-avatar-dot"
                    :class="
                      selectedStore.snapshot.isActive
                        ? 'ap__store-dot--ok'
                        : 'ap__store-dot--off'
                    "
                  />
                </div>
                <div class="ap__org-header-body">
                  <h2 class="ap__org-name">{{ selectedStore.designation }}</h2>
                  <div class="ap__org-pills">
                    <span
                      class="ap__pill"
                      :class="
                        selectedStore.snapshot.isActive
                          ? 'ap__pill--ok'
                          : 'ap__pill--off'
                      "
                    >
                      {{
                        selectedStore.snapshot.isActive
                          ? t('analytics.pill.active')
                          : t('analytics.pill.inactive')
                      }}
                    </span>
                    <span
                      class="ap__pill"
                      :class="
                        selectedStore.snapshot.paymentConfigured
                          ? 'ap__pill--ok'
                          : 'ap__pill--warn'
                      "
                    >
                      {{ t('analytics.payment') }}
                      {{ selectedStore.snapshot.paymentConfigured ? '✓' : '✗' }}
                    </span>
                    <span class="ap__pill ap__pill--off"
                      >{{ selectedStore.snapshot.seatCount }}
                      {{
                        selectedStore.snapshot.seatCount > 1
                          ? t('analytics.kpis.seats')
                          : t('analytics.kpis.seat')
                      }}</span
                    >
                  </div>
                </div>
                <div class="ap__view-toggle">
                  <button
                    class="ap__view-btn"
                    :class="{ 'ap__view-btn--active': storeView === 'chart' }"
                    @click="storeView = 'chart'"
                  >
                    <i class="pi pi-chart-bar" />
                  </button>
                  <button
                    class="ap__view-btn"
                    :class="{ 'ap__view-btn--active': storeView === 'table' }"
                    @click="storeView = 'table'"
                  >
                    <i class="pi pi-table" />
                  </button>
                </div>
              </div>

              <div class="ap__kpis">
                <AnalyticsKpiCard
                  v-for="kpi in storeKpis"
                  :key="kpi.label"
                  :icon="kpi.icon"
                  :label="kpi.label"
                  :value="kpi.value"
                  :desc="kpi.desc"
                  :accent="kpi.accent"
                />
              </div>

              <template v-if="storeView === 'chart'">
                <div class="ap__row2">
                  <!-- Classement CA par commerce / Graphique CA par commerce -->
                  <div class="ap__card">
                    <div class="ap__card-hd">
                      <div>
                        <h3 class="ap__card-title">
                          {{ t('analytics.ranking.storeRevTitle') }}
                        </h3>
                        <p class="ap__card-sub">
                          {{ t('analytics.ranking.revenuesPeriod') }} ·
                          {{ PERIODS.find((p) => p.key === period)?.label }}
                          <span class="ap__hint">{{
                            t('analytics.ranking.caHint')
                          }}</span>
                        </p>
                      </div>
                      <div class="ap__rank-hd-right">
                        <span
                          v-if="storeRankMode === 'ranking'"
                          class="ap__badge"
                          >{{ allStoresUnfiltered.length }}
                          {{ t('analytics.ranking.stores') }}</span
                        >
                        <!-- Toggle ranking ↔ graphique -->
                        <div class="ap__rank-toggle">
                          <button
                            class="ap__rank-toggle-btn"
                            :class="{
                              'ap__rank-toggle-btn--active':
                                storeRankMode === 'ranking',
                            }"
                            :title="t('analytics.table.colStore')"
                            @click="storeRankMode = 'ranking'"
                          >
                            <i class="pi pi-bars" />
                          </button>
                          <button
                            class="ap__rank-toggle-btn"
                            :class="{
                              'ap__rank-toggle-btn--active':
                                storeRankMode === 'evolution',
                            }"
                            :title="t('analytics.ranking.storeRevTitle')"
                            @click="storeRankMode = 'evolution'"
                          >
                            <i class="pi pi-chart-bar" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Mode Ranking -->
                    <div v-if="storeRankMode === 'ranking'" class="ap__ranking">
                      <div
                        v-if="storeRanking.length === 0"
                        class="ap__empty-mini"
                      >
                        <i class="pi pi-chart-bar" />
                        <p>{{ t('analytics.noData') }}</p>
                      </div>
                      <div v-else class="ap__rank-list">
                        <button
                          v-for="row in storeRanking"
                          :key="row.id"
                          class="ap__rank-entry"
                          :class="{
                            'ap__rank-entry--active':
                              selectedStoreId === row.id,
                          }"
                          @click="selectedStoreId = row.id"
                        >
                          <div class="ap__rank-entry-top">
                            <div class="ap__rank-entry-left">
                              <span class="ap__rank-num">{{ row.rank }}</span>
                              <span class="ap__rank-name">{{ row.name }}</span>
                            </div>
                            <div class="ap__rank-entry-right">
                              <span
                                class="ap__rank-ca"
                                :class="{
                                  'ap__rank-ca--zero': row.revenue === 0,
                                }"
                              >
                                {{
                                  row.revenue === 0 ? '—' : fmtDec(row.revenue)
                                }}
                              </span>
                              <span class="ap__rank-orders">{{
                                row.orders
                                  ? `${row.orders} ${t('analytics.ranking.colOrders')}`
                                  : '—'
                              }}</span>
                            </div>
                          </div>
                          <div class="ap__rank-bar-bg">
                            <div
                              class="ap__rank-bar-fill"
                              :style="{ width: row.pct + '%' }"
                            />
                          </div>
                        </button>
                      </div>
                    </div>

                    <!-- Mode Graphique -->
                    <div v-else class="ap__evolution">
                      <div
                        v-if="storeBarData.values.length === 0"
                        class="ap__empty-mini"
                      >
                        <i class="pi pi-chart-bar" />
                        <p>{{ t('analytics.noData') }}</p>
                      </div>
                      <AnalyticsBarChart
                        v-else
                        :data="storeBarData.values"
                        :labels="storeBarData.labels"
                      />
                    </div>
                  </div>

                  <!-- Donut paiements store sélectionné -->
                  <div class="ap__card">
                    <div class="ap__card-hd">
                      <div>
                        <h3 class="ap__card-title">
                          {{ t('analytics.payments.title') }}
                        </h3>
                        <p class="ap__card-sub">
                          {{ selectedStore.designation }}
                        </p>
                      </div>
                    </div>
                    <AnalyticsDonutChart
                      v-if="storeDonut.length"
                      :slices="storeDonut"
                    />
                    <div v-else class="ap__empty-mini">
                      <i class="pi pi-chart-pie" />
                      <p>{{ t('analytics.noTx') }}</p>
                    </div>
                    <div class="ap__donut-legend">
                      <div class="ap__legend-row">
                        <span
                          class="ap__legend-dot"
                          style="background: #10b981"
                        />
                        <span class="ap__legend-label">{{
                          t('analytics.payments.success')
                        }}</span>
                        <span class="ap__legend-val">{{
                          selectedStore.windowed.payments.success
                        }}</span>
                      </div>
                      <div class="ap__legend-row">
                        <span
                          class="ap__legend-dot"
                          style="background: #f59e0b"
                        />
                        <span class="ap__legend-label">{{
                          t('analytics.payments.pending')
                        }}</span>
                        <span class="ap__legend-val">{{
                          selectedStore.windowed.payments.pending
                        }}</span>
                      </div>
                      <div class="ap__legend-row">
                        <span
                          class="ap__legend-dot"
                          style="background: #ef4444"
                        />
                        <span class="ap__legend-label">{{
                          t('analytics.payments.failed')
                        }}</span>
                        <span class="ap__legend-val">{{
                          selectedStore.windowed.payments.failed
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <template v-else>
                <div class="ap__card">
                  <div class="ap__card-hd">
                    <div>
                      <h3 class="ap__card-title">
                        {{ t('analytics.table.allStores') }}
                      </h3>
                      <p class="ap__card-sub">
                        {{ allStoresUnfiltered.length }}
                        {{ t('analytics.kpis.stores2') }}
                      </p>
                    </div>
                    <button class="ap__export-btn" @click="exportStores">
                      <i class="pi pi-download" /><span>CSV</span>
                    </button>
                  </div>
                  <div class="ap__table-wrap">
                    <table class="ap__table">
                      <thead>
                        <tr>
                          <th class="ap__th ap__th--name">
                            {{ t('analytics.table.colStore') }}
                          </th>
                          <th class="ap__th">
                            {{ t('analytics.table.colStatus') }}
                          </th>
                          <th class="ap__th">
                            {{ t('analytics.table.colPmt') }}
                          </th>
                          <th class="ap__th">
                            <button
                              class="ap__sort-btn"
                              @click="toggleStoreSort('revenue')"
                            >
                              {{ t('analytics.table.colCa') }}
                              <i
                                :class="`pi ${sortIco(storeSort, 'revenue')}`"
                              />
                            </button>
                          </th>
                          <th class="ap__th">
                            <button
                              class="ap__sort-btn"
                              @click="toggleStoreSort('orders')"
                            >
                              {{ t('analytics.table.colOrders') }}
                              <i
                                :class="`pi ${sortIco(storeSort, 'orders')}`"
                              />
                            </button>
                          </th>
                          <th class="ap__th">
                            {{ t('analytics.table.colCompleted') }}
                          </th>
                          <th class="ap__th">
                            {{ t('analytics.table.colPending') }}
                          </th>
                          <th class="ap__th">
                            {{ t('analytics.table.colRejected') }}
                          </th>
                          <th class="ap__th">
                            <button
                              class="ap__sort-btn"
                              @click="toggleStoreSort('basket')"
                            >
                              {{ t('analytics.table.colBasket') }}
                              <i
                                :class="`pi ${sortIco(storeSort, 'basket')}`"
                              />
                            </button>
                          </th>
                          <th class="ap__th">
                            {{ t('analytics.table.colPmtOk') }}
                          </th>
                          <th class="ap__th">
                            <button
                              class="ap__sort-btn"
                              @click="toggleStoreSort('products')"
                            >
                              {{ t('analytics.table.colProducts') }}
                              <i
                                :class="`pi ${sortIco(storeSort, 'products')}`"
                              />
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="s in sortedStores"
                          :key="s.storeId"
                          class="ap__tr"
                          :class="{
                            'ap__tr--selected': selectedStoreId === s.storeId,
                          }"
                          @click="selectedStoreId = s.storeId"
                        >
                          <td class="ap__td ap__td--name">
                            <span class="ap__name">{{ s.designation }}</span>
                          </td>
                          <td class="ap__td">
                            <span
                              class="ap__pill"
                              :class="
                                s.snapshot.isActive
                                  ? 'ap__pill--ok'
                                  : 'ap__pill--off'
                              "
                              >{{
                                s.snapshot.isActive
                                  ? t('analytics.pill.active')
                                  : t('analytics.pill.inactive')
                              }}</span
                            >
                          </td>
                          <td class="ap__td">
                            <span
                              class="ap__pill"
                              :class="
                                s.snapshot.paymentConfigured
                                  ? 'ap__pill--ok'
                                  : 'ap__pill--warn'
                              "
                              >{{
                                s.snapshot.paymentConfigured ? '✓' : '✗'
                              }}</span
                            >
                          </td>
                          <td class="ap__td ap__td--num ap__td--accent">
                            {{ fmtDec(s.windowed.revenue) }}
                          </td>
                          <td class="ap__td ap__td--num">
                            {{ s.windowed.orderCount }}
                          </td>
                          <td class="ap__td ap__td--num ap__td--ok">
                            {{ s.windowed.ordersByStatus.terminated ?? 0 }}
                          </td>
                          <td class="ap__td ap__td--num ap__td--warn">
                            {{ s.windowed.ordersByStatus.pending ?? 0 }}
                          </td>
                          <td class="ap__td ap__td--num ap__td--err">
                            {{ s.windowed.ordersByStatus.rejected ?? 0 }}
                          </td>
                          <td class="ap__td ap__td--num">
                            {{ fmtDec(s.windowed.averageBasket) }}
                          </td>
                          <td class="ap__td ap__td--num ap__td--ok">
                            {{ s.windowed.payments.success }}
                          </td>
                          <td class="ap__td ap__td--num">
                            {{ s.snapshot.productCount }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </template>
            </template>
            <div v-else class="ap__empty">
              <i class="pi pi-shop" />
              <p>{{ t('analytics.selectStore') }}</p>
            </div>
          </div>
        </div>
      </template>

      <!-- ══════════════════════ COMMANDES ══════════════════════════════════ -->
      <template v-else-if="activeTab === 'orders'">
        <div class="ap__layout">
          <aside class="ap__sidebar">
            <div class="ap__search-wrap">
              <i class="pi pi-search ap__search-ico" />
              <input
                v-model="ordersSearch"
                class="ap__search"
                :placeholder="t('analytics.search')"
                type="search"
              />
            </div>
            <div class="ap__sidebar-ctrl">
              <span class="ap__sidebar-count"
                >{{ filteredOrdersStores.length }}
                {{
                  filteredOrdersStores.length > 1
                    ? t('analytics.sidebar.storesLabel')
                    : t('analytics.sidebar.storeLabel')
                }}</span
              >
              <select v-model="ordersPageSize" class="ap__page-size">
                <option v-for="ps in PAGE_SIZES" :key="ps" :value="ps">
                  {{ ps }} {{ t('analytics.perPage') }}
                </option>
              </select>
            </div>
            <div class="ap__list">
              <div
                v-for="s in pagedOrdersStores"
                :key="s.storeId"
                role="button"
                tabindex="0"
                class="ap__list-item"
                :class="{
                  'ap__list-item--active': ordersSelectedStoreId === s.storeId,
                }"
                @click="
                  ordersSelectedStoreId = s.storeId;
                  scrollSelectedIntoView($event);
                "
                @keydown.enter="
                  ordersSelectedStoreId = s.storeId;
                  scrollSelectedIntoView($event);
                "
                @keydown.space.prevent="
                  ordersSelectedStoreId = s.storeId;
                  scrollSelectedIntoView($event);
                "
              >
                <div class="ap__list-avatar-store">
                  <span class="ap__list-avatar-initials">{{
                    storeInitials(s.designation)
                  }}</span>
                  <span
                    class="ap__list-avatar-dot"
                    :class="
                      s.snapshot.isActive
                        ? 'ap__store-dot--ok'
                        : 'ap__store-dot--off'
                    "
                  />
                </div>
                <div class="ap__list-body">
                  <span class="ap__list-name">{{ s.designation }}</span>
                  <span class="ap__list-meta"
                    >{{ fmtDec(s.windowed.revenue) }} ·
                    {{ s.windowed.orderCount }}
                    {{ t('analytics.table.colOrders') }}</span
                  >
                </div>
                <i
                  v-if="ordersSelectedStoreId === s.storeId"
                  class="pi pi-chevron-right ap__list-chev"
                />
              </div>
              <p
                v-if="filteredOrdersStores.length === 0"
                class="ap__list-empty"
              >
                {{ t('analytics.noResult') }}
              </p>
            </div>
            <div v-if="ordersPageCount > 1" class="ap__pagination">
              <button
                class="ap__pg-btn"
                :disabled="ordersPage === 1"
                @click="ordersPage--"
              >
                <i class="pi pi-chevron-left" />
              </button>
              <span class="ap__pg-info"
                >{{ ordersPage }} / {{ ordersPageCount }}</span
              >
              <button
                class="ap__pg-btn"
                :disabled="ordersPage === ordersPageCount"
                @click="ordersPage++"
              >
                <i class="pi pi-chevron-right" />
              </button>
            </div>
          </aside>

          <div class="ap__main">
            <template v-if="ordersPending">
              <div class="ap__sk-kpis">
                <div v-for="i in 4" :key="i" class="ap__sk ap__sk--kpi" />
              </div>
              <div class="ap__sk ap__sk--chart" />
            </template>
            <div v-else-if="ordersError" class="ap__error">
              <i class="pi pi-exclamation-circle" />
              <p>{{ t('analytics.errorOrders') }}</p>
            </div>
            <template v-else-if="ordersKpis.length">
              <div class="ap__org-header">
                <div class="ap__org-avatar ap__org-avatar--orders">
                  <i class="pi pi-shopping-cart" style="font-size: 1rem" />
                </div>
                <div>
                  <h2 class="ap__org-name">
                    {{
                      allStoresUnfiltered.find(
                        (s) => s.storeId === ordersSelectedStoreId,
                      )?.designation ?? ''
                    }}
                  </h2>
                  <p class="ap__org-meta">
                    {{ t('analytics.kpis.ordersStats') }} ·
                    {{ PERIODS.find((p) => p.key === period)?.label }}
                  </p>
                </div>
              </div>
              <div class="ap__kpis">
                <AnalyticsKpiCard
                  v-for="kpi in ordersKpis"
                  :key="kpi.label"
                  :icon="kpi.icon"
                  :label="kpi.label"
                  :value="kpi.value"
                  :desc="kpi.desc"
                  :accent="kpi.accent"
                />
              </div>
              <div class="ap__row2">
                <div class="ap__card ap__card--chart">
                  <div class="ap__card-hd">
                    <div>
                      <h3 class="ap__card-title">
                        {{ t('analytics.ordersByStatus') }}
                      </h3>
                      <p class="ap__card-sub">
                        {{
                          allStoresUnfiltered.find(
                            (s) => s.storeId === ordersSelectedStoreId,
                          )?.designation
                        }}
                      </p>
                    </div>
                    <span class="ap__badge">{{
                      t('analytics.statusBadge')
                    }}</span>
                  </div>
                  <AnalyticsBarChart
                    v-if="ordersChartData.values.length"
                    :data="ordersChartData.values"
                    :labels="ordersChartData.labels"
                  />
                  <div v-else class="ap__empty-mini">
                    <i class="pi pi-chart-bar" />
                    <p>{{ t('analytics.noOrders') }}</p>
                  </div>
                </div>
                <div class="ap__card">
                  <div class="ap__card-hd">
                    <div>
                      <h3 class="ap__card-title">
                        {{ t('analytics.payments.title') }}
                      </h3>
                      <p class="ap__card-sub">
                        {{ t('analytics.payments.repartition') }}
                      </p>
                    </div>
                  </div>
                  <AnalyticsDonutChart
                    v-if="ordersDonut.length"
                    :slices="ordersDonut"
                  />
                  <div v-else class="ap__empty-mini">
                    <i class="pi pi-chart-pie" />
                    <p>{{ t('analytics.noDataShort') }}</p>
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="ap__empty">
              <i class="pi pi-shopping-cart" />
              <p>{{ t('analytics.selectStore') }}</p>
            </div>
          </div>
        </div>
      </template>

      <template v-else-if="activeTab === 'payments'">
        <div class="ap__main ap__main--full">
          <template v-if="paymentsPending">
            <div class="ap__sk-kpis">
              <div v-for="i in 4" :key="i" class="ap__sk ap__sk--kpi" />
            </div>
            <div class="ap__sk ap__sk--chart" />
          </template>
          <div v-else-if="paymentsError" class="ap__error">
            <i class="pi pi-exclamation-circle" />
            <p>{{ t('analytics.errorPayments') }}</p>
          </div>
          <template v-else-if="paymentsKpis.length">
            <div class="ap__org-header">
              <div class="ap__org-avatar ap__org-avatar--orders">
                <i class="pi pi-wallet" style="font-size: 1rem" />
              </div>
              <div>
                <h2 class="ap__org-name">{{ t('analytics.tabs.payments') }}</h2>
                <p class="ap__org-meta">
                  {{ t('analytics.kpis.paymentsStats') }} ·
                  {{ PERIODS.find((p) => p.key === period)?.label }}
                </p>
              </div>
            </div>
            <div class="ap__kpis">
              <AnalyticsKpiCard
                v-for="kpi in paymentsKpis"
                :key="kpi.label"
                :icon="kpi.icon"
                :label="kpi.label"
                :value="kpi.value"
                :desc="kpi.desc"
                :accent="kpi.accent"
              />
            </div>
            <div class="ap__row2">
              <div class="ap__card ap__card--chart">
                <div class="ap__card-hd">
                  <div>
                    <h3 class="ap__card-title">
                      {{ t('analytics.payments.trend') }}
                    </h3>
                    <p class="ap__card-sub">
                      {{ t('analytics.payments.grossAmount') }}
                    </p>
                  </div>
                  <select v-model="paymentsGranularity" class="ap__page-size">
                    <option value="day">
                      {{ t('analytics.granularity.day') }}
                    </option>
                    <option value="week">
                      {{ t('analytics.granularity.week') }}
                    </option>
                    <option value="month">
                      {{ t('analytics.granularity.month') }}
                    </option>
                  </select>
                </div>
                <AnalyticsLineChart
                  v-if="paymentsChartData.values.length"
                  :data="paymentsChartData.values"
                  :labels="paymentsChartData.labels"
                />
                <div v-else class="ap__empty-mini">
                  <i class="pi pi-chart-line" />
                  <p>{{ t('analytics.noDataShort') }}</p>
                </div>
              </div>
              <div class="ap__card">
                <div class="ap__card-hd">
                  <div>
                    <h3 class="ap__card-title">
                      {{ t('analytics.payments.title') }}
                    </h3>
                    <p class="ap__card-sub">
                      {{ t('analytics.payments.byChannel') }}
                    </p>
                  </div>
                </div>
                <AnalyticsDonutChart
                  v-if="paymentsDonut.length"
                  :slices="paymentsDonut"
                />
                <div v-else class="ap__empty-mini">
                  <i class="pi pi-chart-pie" />
                  <p>{{ t('analytics.noDataShort') }}</p>
                </div>
              </div>
            </div>
          </template>
          <div v-else class="ap__empty">
            <i class="pi pi-wallet" />
            <p>{{ t('analytics.noDataShort') }}</p>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<style scoped>
/* ── Root container — reprend le padding de la référence HTML (p-10) ── */
.ap {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  min-width: 0;
  padding: 2.5rem;
  box-sizing: border-box;
}

/* Header */
.ap__header {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
@media (min-width: 768px) {
  .ap__header {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
}
.ap__title-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
}
.ap__title {
  margin: 0;
  font-size: clamp(2rem, 5vw, 2.5rem);
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #0b1320;
}
.ap__live-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.2rem 0.625rem 0.2rem 0.4rem;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: rgba(148, 163, 184, 0.12);
  color: #94a3b8;
  border: 1px solid rgba(148, 163, 184, 0.2);
  transition:
    background 0.3s,
    color 0.3s,
    border-color 0.3s;
  align-self: center;
}
.ap__live-badge--on {
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
  border-color: rgba(34, 197, 94, 0.25);
}
.ap__live-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #94a3b8;
  flex-shrink: 0;
  transition: background 0.3s;
}
.ap__live-badge--on .ap__live-dot {
  background: #22c55e;
  box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4);
  animation: ap-live-pulse 2s ease-in-out infinite;
}
@keyframes ap-live-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4);
  }
  70% {
    box-shadow: 0 0 0 5px rgba(34, 197, 94, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
  }
}
.ap__sub {
  margin: 0;
  font-size: 1rem;
  font-weight: 400;
  color: #7f775f;
}

/* Periods — pill group glass */
.ap__periods {
  display: flex;
  gap: 0;
  flex-wrap: wrap;
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 0.75rem;
  padding: 0.25rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.ap__period {
  padding: 0.4375rem 1rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  background: none;
  color: #94a3b8;
  transition:
    background 0.15s,
    color 0.15s;
}
.ap__period:hover {
  color: #1a1a1a;
}
.ap__period--active {
  background: #0b1320;
  color: #fff;
  box-shadow: 0 2px 8px rgba(11, 19, 32, 0.2);
}

/* Header right — periods + custom grouped */
.ap__header-right {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
}

/* Custom date range button */
.ap__custom-wrap {
  position: relative;
}
.ap__custom-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4375rem 0.875rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(11, 19, 32, 0.12);
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(8px);
  font-size: 0.75rem;
  font-weight: 700;
  font-family: inherit;
  color: #7f775f;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;
}
.ap__custom-btn:hover {
  background: rgba(255, 255, 255, 0.9);
  color: #0b1320;
}
.ap__custom-btn--active {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.35);
}

/* Popover */
.ap__picker {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  z-index: 100;
  background: #fff;
  border: 1px solid rgba(11, 19, 32, 0.1);
  border-radius: 1rem;
  padding: 1.25rem;
  box-shadow: 0 16px 48px rgba(11, 19, 32, 0.12);
  width: 340px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.ap__picker-title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 700;
  color: #0b1320;
}
.ap__picker-row {
  display: flex;
  align-items: flex-end;
  gap: 0.625rem;
}
.ap__picker-field {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.ap__picker-sep {
  font-size: 0.875rem;
  color: #94a3b8;
  padding-bottom: 0.5rem;
  flex-shrink: 0;
}
.ap__picker-label {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
}
.ap__picker-input {
  width: 100%;
  height: 2.25rem;
  padding: 0 0.75rem;
  border: 1.5px solid rgba(11, 19, 32, 0.12);
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-family: inherit;
  color: #0b1320;
  background: #f8f9ff;
  outline: none;
  box-sizing: border-box;
  cursor: pointer;
  transition: border-color 0.15s;
}
.ap__picker-input:focus {
  border-color: #ffd60a;
  box-shadow: 0 0 0 3px rgba(255, 214, 10, 0.18);
}
.ap__picker-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}
.ap__picker-cancel {
  padding: 0.5rem 1rem;
  border-radius: 0.625rem;
  border: 1px solid rgba(11, 19, 32, 0.12);
  background: transparent;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  color: #7f775f;
  cursor: pointer;
  transition: background 0.15s;
}
.ap__picker-cancel:hover {
  background: rgba(11, 19, 32, 0.04);
}
.ap__picker-apply {
  padding: 0.5rem 1.25rem;
  border-radius: 0.625rem;
  border: none;
  background: #ffd60a;
  color: #0b1320;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.3);
  transition: background 0.15s;
}
.ap__picker-apply:hover:not(:disabled) {
  background: #e6c109;
}
.ap__picker-apply:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.ap__picker-backdrop {
  position: fixed;
  inset: 0;
  z-index: 99;
}

/* Popover transition */
.ap-picker-enter-active,
.ap-picker-leave-active {
  transition:
    opacity 0.15s,
    transform 0.15s;
}
.ap-picker-enter-from,
.ap-picker-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.97);
}

/* Sous-nav tabs */
.ap__subnav {
  display: flex;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  overflow-x: auto;
  scrollbar-width: none;
  gap: 0.5rem;
}
.ap__subnav::-webkit-scrollbar {
  display: none;
}
.ap__tab {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 0.5rem;
  border: none;
  background: none;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  color: #94a3b8;
  cursor: pointer;
  white-space: nowrap;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition:
    color 0.15s,
    border-color 0.15s;
}
.ap__tab .pi {
  font-size: 0.875rem;
}
.ap__tab:hover {
  color: #0b1320;
}
.ap__tab--active {
  color: #0b1320;
  border-bottom-color: #ffd60a;
  font-weight: 700;
}

/* Layout : grille 12 col comme HTML référence (col-span-3 / col-span-9) */
.ap__layout {
  display: grid;
  grid-template-columns: 3fr 9fr;
  gap: 2rem;
  align-items: start;
  min-width: 0;
  height: calc(100vh - 260px);
  min-height: 400px;
}
@media (max-width: 900px) {
  .ap__layout {
    grid-template-columns: 1fr;
    height: auto;
  }
}

/* Sidebar — glass card (h-[700px] comme référence) */
.ap__sidebar {
  width: 100%;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.5rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
  display: flex;
  flex-direction: column;
  height: 700px;
  overflow: hidden;
  padding: 1.5rem;
  box-sizing: border-box;
}
@media (max-width: 900px) {
  .ap__sidebar {
    width: 100%;
    max-width: none;
    height: auto;
  }
}

.ap__search-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.5);
  margin-bottom: 1rem;
}
.ap__search-ico {
  font-size: 0.875rem;
  color: #94a3b8;
  flex-shrink: 0;
}
.ap__search {
  flex: 1;
  border: none;
  background: none;
  font: inherit;
  font-size: 0.8125rem;
  color: #1a1a1a;
  outline: none;
  min-width: 0;
}
.ap__search::placeholder {
  color: #94a3b8;
}
.ap__search::-webkit-search-cancel-button {
  cursor: pointer;
}

.ap__list {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  padding-right: 0.375rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.1) transparent;
}
.ap__list::-webkit-scrollbar {
  width: 3px;
}
.ap__list::-webkit-scrollbar-track {
  background: transparent;
}
.ap__list::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 9999px;
}
.ap__list::-webkit-scrollbar-thumb:hover {
  background: #cbd5e1;
}

.ap__list-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 0.75rem;
  border: 1px solid transparent;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
}
.ap__list-item:hover {
  background: rgba(255, 255, 255, 0.85);
}
.ap__list-item:focus-visible {
  outline: 2px solid var(--nxt-primary, #141c29);
  outline-offset: 2px;
}
.ap__list-item--active {
  background: #ffd60a;
  border-color: rgba(255, 214, 10, 0.4);
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.2);
}
.ap__list-avatar {
  width: 2.5rem;
  height: 2.5rem;
  min-width: 2.5rem;
  border-radius: 0.75rem;
  background: #e7eeff;
  color: #0b1320;
  border: 1.5px solid rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 800;
  flex-shrink: 0;
  letter-spacing: 0.02em;
  box-shadow: none;
}
.ap__list-item--active .ap__list-avatar {
  background: #0b1320;
  color: #fff;
  border-color: transparent;
  box-shadow: 0 2px 8px rgba(11, 19, 32, 0.25);
}
.ap__list-avatar--store {
  background: #f1f5f9;
  color: #64748b;
  box-shadow: none;
  border: 1.5px solid rgba(0, 0, 0, 0.06);
}
.ap__list-item--active .ap__list-avatar--store {
  background: #ffd60a;
  color: #0b1320;
  border-color: rgba(255, 214, 10, 0.4);
}
.ap__list-body {
  flex: 1;
  min-width: 0;
}
.ap__list-name {
  display: block;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #1a1a1a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ap__list-meta {
  display: block;
  font-size: 0.6875rem;
  font-weight: 500;
  color: #94a3b8;
  margin-top: 0.1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ap__list-item--active .ap__list-name {
  color: #0b1320 !important;
  font-weight: 800;
}
.ap__list-item--active .ap__list-meta {
  color: rgba(11, 19, 32, 0.55) !important;
}
.ap__list-chev {
  font-size: 0.625rem;
  color: #94a3b8;
  flex-shrink: 0;
}
.ap__list-empty {
  text-align: center;
  padding: 1.5rem 1rem;
  font-size: 0.8125rem;
  color: #94a3b8;
  font-weight: 500;
  margin: 0;
}

/* Main — zone scrollable à droite */
.ap__main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  height: 700px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.5rem;
  background: transparent;
  scrollbar-width: thin;
  scrollbar-color: #e2e8f0 transparent;
}
.ap__main--full {
  width: 100%;
}
.ap__main::-webkit-scrollbar {
  width: 4px;
}
.ap__main::-webkit-scrollbar-track {
  background: transparent;
}
.ap__main::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 9999px;
}
.ap__main::-webkit-scrollbar-thumb:hover {
  background: #cbd5e1;
}

/* Org header — glass card arrondie (référence: rounded-[32px] p-8) */
.ap__org-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 2rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}
.ap__org-header:hover {
  border-color: rgba(255, 214, 10, 0.4);
  box-shadow: 0 10px 30px -10px rgba(11, 19, 32, 0.05);
}
.ap__org-avatar {
  width: 4rem;
  height: 4rem;
  min-width: 4rem;
  border-radius: 1rem;
  background: #0b1320;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 800;
  flex-shrink: 0;
  box-shadow: 0 4px 20px rgba(11, 19, 32, 0.2);
}
.ap__org-avatar--store {
  background: #f1f5f9;
  color: #64748b;
  box-shadow: none;
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.ap__org-avatar--orders {
  background: #f8f5ef;
  color: #92400e;
  box-shadow: none;
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.ap__org-name {
  margin: 0 0 0.15rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: #0b1320;
  letter-spacing: -0.01em;
}
.ap__org-meta {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
}
.ap__org-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.25rem;
}

/* Onboarding zone — centrée dans le header org */
.ap__org-header-body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.ap__onboarding-center {
  flex: 1;
  min-width: 120px;
  max-width: 240px;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.ap__onboarding-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
}
.ap__onboarding-label {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  white-space: nowrap;
}
.ap__onboarding-steps {
  font-size: 0.625rem;
  font-weight: 700;
  color: #94a3b8;
  white-space: nowrap;
}
.ap__onboarding-bar {
  width: 100%;
  height: 6px;
  background: #f1f5f9;
  border-radius: 9999px;
  overflow: hidden;
}
.ap__onboarding-fill {
  height: 100%;
  border-radius: 9999px;
  transition: width 0.5s ease;
}
.ap__onboarding-fill--done {
  background: #34d399;
}
.ap__onboarding-fill--mid {
  background: #fbbf24;
}
.ap__onboarding-fill--low {
  background: #f87171;
}

/* Toggle graphique / tableau */
.ap__view-toggle {
  display: flex;
  gap: 0.25rem;
  margin-left: auto;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 0.625rem;
  padding: 0.2rem;
}
.ap__view-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 0.5rem;
  background: none;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.12s;
}
.ap__view-btn .pi {
  font-size: 0.875rem;
}
.ap__view-btn:hover {
  color: #1a1a1a;
}
.ap__view-btn--active {
  background: #fff;
  color: #1a1a1a;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

/* KPIs — grille bento 3 colonnes (comme référence grid-cols-3 gap-6) */
.ap__kpis {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(2, 1fr);
}
@media (min-width: 600px) {
  .ap__kpis {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* Store chips */
.ap__store-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.ap__store-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.35rem 0.75rem;
  border-radius: 9999px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 0.8125rem;
  font-weight: 500;
  color: #64748b;
  background: rgba(255, 255, 255, 0.6);
}
.ap__skel-line {
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.06);
  animation: ap-skel-pulse 1.4s ease-in-out infinite;
}
.ap__store-chip-skel {
  width: 6rem;
  height: 1.75rem;
  border-radius: 9999px;
}
@keyframes ap-skel-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
}
[data-theme='dark'] .ap__skel-line {
  background: rgba(255, 255, 255, 0.08);
}

.ap__chip-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;
}
.ap__chip-dot--ok {
  background: #34d399;
}
.ap__chip-dot--off {
  background: #cbd5e1;
}

/* Row 2 — référence: grid-cols-3 gap-8 (col-span-2 + col-span-1) */
.ap__row2 {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}
@media (min-width: 700px) {
  .ap__row2 {
    grid-template-columns: 2fr 1fr;
  }
}

/* Card — glass (référence: rounded-[32px] p-8 shadow-sm refractive-border) */
.ap__card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 2rem;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}
.ap__card:hover {
  border-color: rgba(255, 214, 10, 0.4);
  box-shadow: 0 10px 30px -10px rgba(11, 19, 32, 0.05);
}
.ap__card--chart {
  overflow: hidden;
}
.ap__card-hd {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}
.ap__card-title {
  margin: 0 0 0.25rem;
  font-size: 1.125rem;
  font-weight: 700;
  color: #0b1320;
}
.ap__card-sub {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  font-weight: 400;
  color: #7f775f;
}
.ap__badge {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  background: #e7eeff;
  color: #4d4632;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  flex-shrink: 0;
  white-space: nowrap;
}
.ap__export-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.875rem;
  border-radius: 0.625rem;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 0.75rem;
  font-weight: 600;
  font-family: inherit;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
}
.ap__export-btn:hover {
  background: #fff;
  color: #1a1a1a;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* Tables */
.ap__table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin: 0 -1.5rem;
  padding: 0 1.5rem;
}
.ap__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
  white-space: nowrap;
}
.ap__th {
  padding: 0.5rem 0.75rem;
  text-align: left;
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  background: rgba(248, 250, 252, 0.5);
}
.ap__th--name {
  min-width: 10rem;
  position: sticky;
  left: 0;
  z-index: 1;
}
.ap__sort-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  padding: 0;
}
.ap__sort-btn:hover {
  color: #1a1a1a;
}
.ap__sort-btn .pi {
  font-size: 0.55rem;
}
.ap__tr {
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  transition: background 0.1s;
  cursor: pointer;
}
.ap__tr:last-child {
  border-bottom: none;
}
.ap__tr:hover {
  background: rgba(255, 255, 255, 0.5);
}
.ap__tr--selected {
  background: rgba(255, 255, 255, 0.8);
}
.ap__td {
  padding: 0.625rem 0.75rem;
  color: #1a1a1a;
  vertical-align: middle;
}
.ap__td--name {
  position: sticky;
  left: 0;
  background: rgba(255, 255, 255, 0.7);
  z-index: 1;
}
.ap__tr:hover .ap__td--name {
  background: rgba(255, 255, 255, 0.85);
}
.ap__td--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.ap__td--accent {
  font-weight: 700;
  color: #111;
}
.ap__td--ok {
  color: #059669;
  font-weight: 600;
}
.ap__td--warn {
  color: #d97706;
  font-weight: 600;
}
.ap__td--err {
  color: #dc2626;
  font-weight: 600;
}
.ap__name {
  font-weight: 600;
}

/* Pills */
.ap__pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.2rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.ap__pill--ok {
  background: rgba(52, 211, 153, 0.15);
  color: #059669;
}
.ap__pill--off {
  background: rgba(0, 0, 0, 0.05);
  color: #94a3b8;
}
.ap__pill--warn {
  background: rgba(251, 191, 36, 0.15);
  color: #d97706;
}

/* Skeleton */
.ap__sk-kpis {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}
.ap__sk-kpis .ap__sk--kpi {
  flex: 1 1 calc(25% - 0.5rem);
  min-width: 130px;
}
.ap__sk {
  border-radius: 1rem;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.4) 25%,
    rgba(255, 255, 255, 0.7) 50%,
    rgba(255, 255, 255, 0.4) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
.ap__sk--search {
  height: 2.5rem;
  margin: 0.5rem;
  border-radius: 0.5rem;
}
.ap__sk--item {
  height: 3.5rem;
  margin: 0.25rem 0.5rem;
  border-radius: 0.75rem;
}
.ap__sk--kpi {
  height: 6.5rem;
}
.ap__sk--chart {
  height: 15rem;
  border-radius: 1rem;
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Erreur / Empty */
.ap__error,
.ap__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3rem;
  text-align: center;
  color: #94a3b8;
  font-size: 0.9375rem;
  font-weight: 600;
}
.ap__error {
  color: #ef4444;
}
.ap__error .pi,
.ap__empty .pi {
  font-size: 2rem;
}
.ap__empty p {
  margin: 0;
}
.ap__empty-mini {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem;
  text-align: center;
  color: #94a3b8;
  font-size: 0.875rem;
}
.ap__empty-mini .pi {
  font-size: 1.5rem;
}
.ap__empty-mini p {
  margin: 0;
}

/* Sidebar ctrl (count + page size) */
.ap__sidebar-ctrl {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0 0.75rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  margin-bottom: 0.5rem;
}
.ap__sidebar-count {
  font-size: 0.625rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.ap__page-size {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 0.375rem;
  background: rgba(255, 255, 255, 0.8);
  color: #64748b;
  font: inherit;
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.2rem 0.375rem;
  cursor: pointer;
  outline: none;
}
.ap__page-size:focus {
  border-color: #1a1a1a;
}

/* Pagination footer sidebar */
.ap__pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}
.ap__pg-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(255, 255, 255, 0.6);
  color: #64748b;
  cursor: pointer;
  font: inherit;
  transition: all 0.12s;
  flex-shrink: 0;
}
.ap__pg-btn:hover:not(:disabled) {
  background: #fff;
  color: #1a1a1a;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}
.ap__pg-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.ap__pg-btn .pi {
  font-size: 0.6875rem;
}
.ap__pg-info {
  font-size: 0.75rem;
  font-weight: 600;
  color: #94a3b8;
  min-width: 3.5rem;
  text-align: center;
}

/* Ranking CA list — style référence HTML (space-y-6, barre h-2) */
.ap__ranking {
  display: flex;
  flex-direction: column;
}
.ap__rank-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.ap__rank-entry {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  background: none;
  border: none;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  padding: 0.25rem 0;
  border-radius: 0.5rem;
  transition: opacity 0.15s;
}
.ap__rank-entry:hover {
  opacity: 0.85;
}
.ap__rank-entry--active .ap__rank-name {
  color: #0b1320;
  font-weight: 800;
}
.ap__rank-entry-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}
.ap__rank-entry-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  flex: 1;
}
.ap__rank-entry-right {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
}
.ap__rank-num {
  font-size: 0.625rem;
  font-weight: 700;
  color: #94a3b8;
  min-width: 1rem;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
}
.ap__rank-name {
  font-size: 0.75rem;
  font-weight: 700;
  color: #0b1320;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.ap__rank-bar-bg {
  height: 8px;
  background: #e7eeff;
  border-radius: 9999px;
  overflow: hidden;
  width: 100%;
}
.ap__rank-bar-fill {
  height: 100%;
  background: #0b1320;
  border-radius: 9999px;
  transition: width 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}
.ap__rank-entry--active .ap__rank-bar-fill {
  background: #ffd60a;
}
.ap__rank-ca {
  font-size: 0.75rem;
  font-weight: 700;
  color: #0b1320;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.ap__rank-ca--zero {
  color: #94a3b8;
  font-weight: 500;
}
.ap__rank-orders {
  font-size: 0.625rem;
  color: #94a3b8;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  min-width: 3rem;
  text-align: right;
}

/* Toggle ranking ↔ évolution */
.ap__rank-hd-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}
.ap__rank-toggle {
  display: flex;
  align-items: center;
  gap: 2px;
  background: rgba(11, 19, 32, 0.05);
  border-radius: 0.5rem;
  padding: 2px;
}
.ap__rank-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.375rem;
  border: none;
  background: none;
  font-size: 0.8rem;
  color: rgba(11, 19, 32, 0.4);
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;
}
.ap__rank-toggle-btn:hover {
  color: #0b1320;
}
.ap__rank-toggle-btn--active {
  background: #0b1320;
  color: #fff;
}

/* Mode Évolution CA */
.ap__evolution {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
}
.ap__evolution-kpi {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}
.ap__evolution-total {
  font-size: 1.5rem;
  font-weight: 800;
  color: #0b1320;
  letter-spacing: -0.02em;
  line-height: 1.1;
}
.ap__evolution-sub {
  font-size: 0.6875rem;
  color: rgba(11, 19, 32, 0.45);
  font-weight: 500;
}

/* Avatar initiales dans la sidebar Commerces */
.ap__list-avatar-store {
  position: relative;
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  background: #f1f5f9;
  color: #475569;
  border: 1.5px solid rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;
}
.ap__list-item--active .ap__list-avatar-store {
  background: #141c29;
  color: #fff;
  border-color: transparent;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
}
.ap__list-avatar-initials {
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  pointer-events: none;
}
.ap__list-avatar-dot {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 2px solid #fff;
}

/* Store dot (partagé) */
.ap__store-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;
}
.ap__store-dot--ok {
  background: #34d399;
}
.ap__store-dot--off {
  background: #cbd5e1;
}

/* Store avatar avec initiales + dot statut */
.ap__store-avatar {
  position: relative;
  flex-shrink: 0;
  width: 3rem;
  height: 3rem;
  border-radius: 0.75rem;
  background: #f1f5f9;
  border: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
}
.ap__store-avatar-initials {
  font-size: 0.875rem;
  font-weight: 800;
  color: #475569;
  letter-spacing: -0.01em;
}
.ap__store-avatar-dot {
  position: absolute;
  bottom: -3px;
  right: -3px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #fff;
}

/* Hint CA explication */
.ap__hint {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.6875rem;
  font-style: italic;
  color: #94a3b8;
}

/* Donut legend */
.ap__donut-legend {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}
.ap__legend-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.ap__legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.ap__legend-label {
  flex: 1;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #64748b;
}
.ap__legend-val {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #1a1a1a;
  font-variant-numeric: tabular-nums;
}
</style>

<style>
/* Dark mode — glass adapté */
[data-theme='dark'] .ap__sidebar {
  background: rgba(20, 28, 41, 0.85);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ap__card {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ap__card:hover {
  border-color: rgba(255, 214, 10, 0.35);
}
[data-theme='dark'] .ap__org-header {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ap__org-header:hover {
  border-color: rgba(255, 214, 10, 0.35);
}
[data-theme='dark'] .ap__periods {
  background: rgba(30, 32, 36, 0.6);
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .ap__period--active {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}
[data-theme='dark'] .ap__period {
  color: rgba(255, 255, 255, 0.45);
}
[data-theme='dark'] .ap__period:hover {
  color: #fff;
}
[data-theme='dark'] .ap__live-badge {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ap__live-badge--on {
  background: rgba(34, 197, 94, 0.12);
  color: #4ade80;
  border-color: rgba(34, 197, 94, 0.2);
}
[data-theme='dark'] .ap__live-badge--on .ap__live-dot {
  background: #4ade80;
}
[data-theme='dark'] .ap__custom-btn {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .ap__custom-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .ap__custom-btn--active {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
}
[data-theme='dark'] .ap__picker {
  background: #111c2e;
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.4);
}
[data-theme='dark'] .ap__picker-title {
  color: #f1f5f9;
}
[data-theme='dark'] .ap__picker-input {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
  color: #f1f5f9;
}
[data-theme='dark'] .ap__picker-input:focus {
  border-color: #ffd60a;
  box-shadow: 0 0 0 3px rgba(255, 214, 10, 0.15);
}
[data-theme='dark'] .ap__picker-cancel {
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .ap__subnav {
  border-bottom-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .ap__tab {
  color: rgba(255, 255, 255, 0.45);
}
[data-theme='dark'] .ap__tab:hover {
  color: #fff;
}
[data-theme='dark'] .ap__tab--active {
  color: #ffd60a;
  border-bottom-color: #ffd60a;
}
[data-theme='dark'] .ap__search-wrap {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ap__search {
  color: #fff;
}
[data-theme='dark'] .ap__list-item {
  background: rgba(255, 255, 255, 0.05);
}
[data-theme='dark'] .ap__list-item:hover {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ap__list-item--active,
[data-theme='dark'] .ap__list-item--active:hover {
  background: #ffd60a;
  border-color: rgba(255, 214, 10, 0.5);
}
[data-theme='dark'] .ap__list-name {
  color: #fff;
}
[data-theme='dark'] .ap__list-item--active .ap__list-name {
  color: #0b1320 !important;
  font-weight: 800;
}
[data-theme='dark'] .ap__list-item--active .ap__list-meta {
  color: rgba(11, 19, 32, 0.55) !important;
}
[data-theme='dark'] .ap__view-toggle {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ap__view-btn--active {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}
[data-theme='dark'] .ap__th {
  background: rgba(255, 255, 255, 0.03);
  color: rgba(255, 255, 255, 0.4);
  border-bottom-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .ap__td {
  color: #fff;
}
[data-theme='dark'] .ap__td--name {
  background: rgba(30, 32, 36, 0.8);
}
[data-theme='dark'] .ap__tr:hover .ap__td--name {
  background: rgba(255, 255, 255, 0.04);
}
[data-theme='dark'] .ap__tr--selected {
  background: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .ap__tr:hover {
  background: rgba(255, 255, 255, 0.04);
}
[data-theme='dark'] .ap__sk {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.09) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
[data-theme='dark'] .ap__sidebar-ctrl {
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ap__page-size {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.7);
}
[data-theme='dark'] .ap__pagination {
  border-top-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .ap__pg-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .ap__pg-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .ap__list-avatar-store {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: none;
}
[data-theme='dark'] .ap__list-item--active .ap__list-avatar-store {
  background: #ffd60a;
  color: #0b1320;
  border-color: rgba(255, 214, 10, 0.4);
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.2);
}
[data-theme='dark'] .ap__list-avatar-dot {
  border-color: rgba(8, 14, 24, 0.9);
}
[data-theme='dark'] .ap__onboarding-bar {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ap__onboarding-label {
  color: rgba(255, 255, 255, 0.55);
}
[data-theme='dark'] .ap__onboarding-steps {
  color: rgba(255, 255, 255, 0.35);
}
[data-theme='dark'] .ap__rank-bar-bg {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ap__rank-bar-fill {
  background: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .ap__rank-entry--active .ap__rank-bar-fill {
  background: #ffd60a;
}
[data-theme='dark'] .ap__rank-toggle {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ap__rank-toggle-btn {
  color: rgba(255, 255, 255, 0.35);
}
[data-theme='dark'] .ap__rank-toggle-btn:hover {
  color: #fff;
}
[data-theme='dark'] .ap__rank-toggle-btn--active {
  background: rgba(255, 255, 255, 0.15);
  color: #ffd60a;
}
[data-theme='dark'] .ap__evolution-total {
  color: #f1f5f9;
}
[data-theme='dark'] .ap__evolution-sub {
  color: rgba(255, 255, 255, 0.35);
}
[data-theme='dark'] .ap__rank-name {
  color: #f1f5f9;
}
[data-theme='dark'] .ap__rank-ca {
  color: #f1f5f9;
}
[data-theme='dark'] .ap__donut-legend {
  border-top-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .ap__legend-label {
  color: rgba(255, 255, 255, 0.55);
}
[data-theme='dark'] .ap__legend-val {
  color: #fff;
}
[data-theme='dark'] .ap__title {
  color: #f1f5f9;
}
[data-theme='dark'] .ap__sub {
  color: rgba(255, 255, 255, 0.45);
}
[data-theme='dark'] .ap__org-name {
  color: #f1f5f9;
}
[data-theme='dark'] .ap__org-meta {
  color: rgba(255, 255, 255, 0.45);
}
[data-theme='dark'] .ap__card-title {
  color: #f1f5f9;
}
[data-theme='dark'] .ap__list-avatar {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: none;
}
[data-theme='dark'] .ap__list-item--active .ap__list-avatar {
  background: #ffd60a;
  color: #0b1320;
  border-color: rgba(255, 214, 10, 0.4);
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.2);
}
[data-theme='dark'] .ap__main {
  background: transparent;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}
[data-theme='dark'] .ap__main::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .ap__list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ap__store-avatar {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ap__store-avatar-initials {
  color: rgba(255, 255, 255, 0.7);
}
[data-theme='dark'] .ap__store-avatar-dot {
  border-color: rgba(30, 32, 36, 0.9);
}
[data-theme='dark'] .ap__badge {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .ap__card-sub {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .ap__hint {
  color: rgba(255, 255, 255, 0.3);
}
[data-theme='dark'] .ap__rank-num {
  color: rgba(255, 255, 255, 0.35);
}
[data-theme='dark'] .ap__rank-orders {
  color: rgba(255, 255, 255, 0.35);
}
</style>
